// English translations

export default {
  settings: {
    title: "System Settings",
    description: "Manage all system settings from one place efficiently and easily",
    refresh: "Refresh Data",
    selectSection: "Select a section to view",
    selectSectionDescription: "Click on one of the buttons above to view the desired section content",
    general: "General Settings",
    generalDesc: "Company settings and working hours",
    theme: {
      sidebarOrder: {
        title: "Customize Sidebar Order",
        description: "Drag items to reorder them from most important to least important",
        tutorial: "Tutorial",
        reset: "Reset",
        howToSort: "How to Sort Sidebar Items:",
        step1: "Click and hold the grip icon (⋮⋮) on any item",
        step2: "Drag it up or down to your preferred position",
        step3: "Release to drop. The order is saved automatically!",
        example: "Example:",
        moveUp: "Move up",
        moveDown: "Move down",
        orderSavedAuto: "Order saved automatically",
        savedSuccess: "Sidebar order saved successfully!",
        resetSuccess: "Sidebar order reset to default!",
        resetConfirmTitle: "Reset Sidebar Order?",
        resetConfirmMessage: "Are you sure you want to reset the sidebar order to default? This action cannot be undone.",
        cancel: "Cancel"
      }
    },
    themeDesc: "Customize colors and appearance",
    location: "Location",
    locationDesc: "Manage company locations",
    departments: "Company Departments",
    departmentsDesc: "Define company departments",
    deduction: "Delay Settings",
    deductionDesc: "Delay deduction rules",
    holidays: "Official Holidays",
    holidaysDesc: "Manage the company's official holiday calendar",
    ai: "AI Settings",
    aiDesc: "AI model settings",
    language: "Language Settings",
    languageDesc: "Choose interface language",
    company: "Company Information",
    companyDesc: "View and edit company information",
    help: {
      timezone: "Used for attendance, payroll, and notifications. Pick the timezone of the company’s real location.",
      workStart: "Default organization start time. If an employee has a shift, the shift start time is used instead.",
      workEnd: "Default organization end time. An assigned shift takes priority when present.",
      gracePeriod: "Minutes after shift start before someone is marked late. Note: if the employee has a shift, that shift’s grace period is used first.",
      workDays: "Weekdays counted as official work days for attendance, absence, and payroll.",
      annualLeaveDays: "Annual leave days granted to each employee per year.",
      sickLeaveDays: "Sick leave days allowed per year.",
      leaveSettings: "Annual and sick leave balances, and whether unused days roll into the next year.",
      carryForward: "Allow unused annual leave to roll into the next year. If off, remaining balance is lost at year end.",
      maxCarryForward: "Maximum days that can roll into the next year when carry-forward is enabled. Anything above this cap is not carried.",
      overtimeEnabled: "Enable overtime pay after official shift hours.",
      overtimeMultiplier: "Overtime hour pay: one hour, two hours, or more.",
      overtimeMinimumHours: "Minimum whole overtime hours before pay starts.",
      overtimeCalculationMethod: "How overtime hours are aggregated before calculation: daily, weekly, or monthly.",
      attendanceExempt: "Exempt selected work-location types from check-in/out (e.g. delivery or remote).",
      absencePenalty: "Salary days deducted for unauthorized absence.",
      shifts: "A shift defines work hours and grace period for assigned employees, and overrides organization defaults.",
      shiftGracePeriod: "Grace period for this shift only. After it ends, status becomes Late. Money deductions are configured separately under Delay Settings.",
      latePenaltyEnabled: "Enable automatic late deductions using the rules below. Being marked Late and getting a money deduction are not the same thing.",
      latePenaltyRules: "Each rule maps minutes late (from shift start) to a deduction type: quarter / half / full day or a custom amount.",
      delayMinutes: "Minutes from shift start at which this rule starts applying.",
      penaltyType: "Deduction type: a fraction of daily salary (quarter/half/full) or a custom amount.",
      autoCheckoutEnabled: "Automatically check out employees who forget to clock out after shift end.",
      autoCheckoutGraceHours: "How many hours after shift end to wait before auto check-out.",
      checkInSelfieMode: "Whether check-in selfie is off, optional, or required for employees in the selected work types.",
      checkInSelfieApplyTo: "Which employee work types the selfie policy applies to (office, remote, hybrid, delivery).",
      autoCheckoutCountOvertime: "Whether hours between shift end and auto check-out count as overtime.",
      autoCheckoutDeductionPolicy: "Deduction applied when the system auto-checks out someone who forgot to clock out. Branches can follow this or set their own.",
      sectionDeduction: "Configure late-penalty rules (e.g. after 30 minutes = quarter day).",
      sectionHolidays: "Add official holidays so those days are not counted as absence.",
      sectionLanguage: "Change the interface language (Arabic / English / German).",
      sectionCompany: "Company profile, work hours, shifts, leave, and overtime settings.",
    },
    generalSettings: {
      title: "General Settings",
      description: "Manage company settings and official working hours and breaks",
      workDays: "work days",
      infoTitle: "About Settings",
      infoDescription: "You can manage all general settings of the institution through this page, including working hours, breaks, and weekly work days.",
      generalInfo: "General Information",
      companyName: "Company Name",
      companyNamePlaceholder: "Enter company name",
      timezone: "Timezone",
      timezonePlaceholder: "Select timezone",
      gracePeriod: "Grace Period (minutes)",
      gracePeriodPlaceholder: "Example: 30",
      minPresentHours: "Minimum Present Hours",
      minPresentHoursPlaceholder: "Example: 4",
      overtimeRate: "Overtime Rate",
      overtimeRatePlaceholder: "Example: 1.5",
      dailyWorkingHours: "Daily Working Hours",
      attendanceAdjustment: "Enable automatic lateness deduction and overtime bonus",
      remoteDeduction: "Enable lateness deduction for remote employees",
      workHours: "Work Schedule",
      startTime: "Start Time",
      endTime: "End Time",
      workDaysLabel: "Work Days",
      selectAtLeastOneWorkDay: "Please select at least one work day",
      breaks: "Set Daily Break Times",
      addBreak: "Add Break",
      breakNumber: "Break",
      from: "From",
      to: "To",
      startTimePlaceholder: "Start time",
      endTimePlaceholder: "End time",
      workTimeRangeRequired: "Please set work start and end times",
      workTimeRangeInvalidFormat: "Invalid time format (HH:mm)",
      workTimeRangeSame: "Work start and end cannot be the same",
      documentManagement: "Document Management",
      documentDescription: "These documents will be sent to all employees",
      uploadNewDocument: "Upload New Document",
      uploadedDocuments: "Uploaded Documents",
      noDocuments: "No documents uploaded",
      size: "Size",
      uploadDate: "Upload Date",
      delete: "Delete",
      uploading: "Uploading...",
      uploadDocument: "Upload Document",
      refreshData: "Refresh Data",
      saveChanges: "Save Changes",
      editMode: "Edit Mode",
      loading: "Loading...",
      hours: "hours",
      minutes: "minute",
      hoursAndMinutes: "hours and",
      tokenNotFound: "Authentication token not found",
      loadFailed: "Failed to load work hours settings",
      settingsNotFound: "Work settings not found",
      settingsLoaded: "Settings loaded successfully",
      updateFailed: "Failed to update work hours settings",
      settingsSaved: "Settings saved successfully",
      filesLoadFailed: "Failed to load document list",
      filesLoaded: "Document list loaded successfully",
      selectFile: "Please select a file to upload",
      uploadFailed: "Failed to upload document",
      uploadSuccess: "Document uploaded successfully",
      deleteFailed: "Failed to delete document",
      deleteSuccess: "Document deleted successfully"
    },

    shifts: {
      title: "Shifts",
      description: "Define multiple shifts (morning / evening / night), then assign each employee from details or the approval wizard.",
      orgWide: "Company-wide",
      branchDescription: "Define shifts for this branch. Employees can be assigned to branch shifts or company-wide shifts.",
      branchInheritDescription: "This branch inherits company shifts (preview only). Choose “Customize branch shifts” to add branch-specific shifts.",
      add: "Add shift",
      edit: "Edit shift",
      name: "Shift name",
      namePlaceholder: "e.g. Morning",
      nameRequired: "Shift name is required (min 2 characters)",
      workStart: "Shift start",
      workEnd: "Shift end",
      gracePeriod: "Grace period (minutes)",
      gracePeriodShort: "Grace {minutes} min",
      active: "Active",
      inactive: "Inactive",
      loading: "Loading shifts...",
      empty: "No shifts yet. Add shifts if the company has more than one work schedule.",
      loadFailed: "Failed to load shifts",
      createSuccess: "Shift created successfully",
      updateSuccess: "Shift updated successfully",
      saveFailed: "Failed to save shift",
      deleteConfirm: "Delete shift \"{name}\"?",
      deleteSuccess: "Shift deleted successfully",
      deleteFailed: "Failed to delete shift",
      deactivated: "Shift deactivated because employees are assigned",
      employeesCount: "{count} employees",
    },
    companyInformation: {
      title: "Company Information",
      description: "View and edit basic company information",
      noData: "No company data available",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      basicInfo: "Basic Information",
      companyName: "Company Name",
      companyNamePlaceholder: "e.g. Teamin",
      companyCode: "Company Code",
      descriptionLabel: "Description",
      descriptionPlaceholder: "e.g. Attendance and HR management company",
      industry: "Industry",
      industryPlaceholder: "e.g. Information Technology",
      contactInfo: "Contact Information",
      email: "Email",
      emailPlaceholder: "e.g. info@company.com",
      phone: "Phone",
      phonePlaceholder: "e.g. 1012345678",
      website: "Website",
      websitePlaceholder: "e.g. https://example.com",
      address: "Address",
      street: "Street",
      streetPlaceholder: "e.g. 123 Nile Street",
      city: "City",
      cityPlaceholder: "e.g. Cairo",
      state: "State",
      statePlaceholder: "e.g. Giza",
      country: "Country",
      countryPlaceholder: "e.g. Egypt",
      postalCode: "Postal Code",
      postalCodePlaceholder: "e.g. 11511",
      gracePeriodPlaceholder: "e.g. 15",
      dateFormatPlaceholder: "e.g. DD-MM-YYYY",
      annualLeaveDaysPlaceholder: "e.g. 21",
      sickLeaveDaysPlaceholder: "e.g. 30",
      maxCarryForwardPlaceholder: "e.g. 7",
      absencePenaltyDaysPlaceholder: "e.g. 1",
      latitudePlaceholder: "e.g. 30.0444",
      longitudePlaceholder: "e.g. 31.2357",
      radiusPlaceholder: "e.g. 150",
      organizationSettings: "Organization Settings",
      timezone: "Timezone",
      selectTimezone: "Select timezone",
      workStart: "Work Start",
      workEnd: "Work End",
      gracePeriod: "Grace Period",
      inMinutes: "in minutes",
      minutes: "minutes",
      workDays: "Work Days",
      displayFormat: "Display format",
      dateFormat: "Date Format",
      leaveSettings: "Leave Settings",
      annualLeaveDays: "Annual Leave Days",
      sickLeaveDays: "Sick Leave Days",
      carryForward: "Carry Forward",
      maxCarryForward: "Max Carry Forward Days",
      overtimeSettings: "Overtime Settings",
      enabled: "Enabled",
      multiplier: "Overtime hour",
      minimumHours: "Minimum Hours",
      overtimeHoursNone: "No minimum",
      overtimeHoursOne: "1 hour",
      overtimeHoursTwo: "2 hours",
      overtimeHoursFew: "{n} hours",
      overtimeHoursMany: "{n} hours",
      calculationMethod: "Calculation Method",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      yes: "Yes",
      no: "No",
      on: "On",
      off: "Off",
      companyLocations: "Company Locations",
      companyLocationsDescription: "Manage and view company locations on the map",
      clickMapToSelect: "Click on map to select location",
      selectedLocation: "Selected Location",
      companyLocation: "Company Location",
      locationName: "Location Name",
      locationNamePlaceholder: "e.g., Main Office",
      addressPlaceholder: "e.g., 123 Nile St, Cairo",
      latitude: "Latitude",
      longitude: "Longitude",
      radiusMeters: "Radius (meters)",
      noLocationSet: "No location set",
      getLocationFailed: "Failed to get location",
      getMyCurrentLocation: "Get my current location",
      clickMapOrEnterManually: "Click on map or enter manually",
      nameRequired: "Company name is required",
      phoneInvalid: "Invalid phone number",
      emailInvalid: "Invalid email format",
      websiteInvalid: "Invalid website URL",
      updateSuccess: "Company information updated successfully",
      totalDeductionOnAbsence: "Total deduction on absence: {n} salary {unit}",
      totalSalaryDeductionOnAbsence: "Total salary deduction on absence",
      sunday: "Sunday",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      attendanceExemptTitle: "Exempt from check-in / check-out",
      attendanceExemptHint: "Choose work-location types that do not need to check in or out. Matches the employee’s Work Location field.",
      attendanceExemptEnabled: "Enable exemption",
      attendanceExemptWorkLocations: "Exempt work-location types",
      attendanceExemptWorkLocationsHint: "An employee is exempt only if their work location matches a selected type.",
      attendanceExemptSelectAtLeastOne: "Enable exemption and select at least one type.",
      selectAtLeastOneWorkDay: "Please select at least one work day",
      attendanceExemptNoneSelected: "No types selected",
      workLocationOffice: "Office",
      workLocationRemote: "Remote",
      workLocationHybrid: "Hybrid",
      workLocationDelivery: "Delivery by order",
      absencePenaltyTitle: "Absence Penalty Settings",
      absencePenaltyEnabled: "Enabled",
      absencePenaltyDaysLabel: "Total salary days deducted",
absencePenaltyDaysHint: "This is the total number of salary days deducted when an employee is absent without approved leave, not additional days on top of the absence day. Example: if you enter 2, two days of salary are deducted. When disabled, only one day is deducted.",
      absencePenaltyDaysDisabledHint: "When disabled: only one salary day is deducted (the absence day).",
      absencePenaltyNotifyAdmin: "Notify manager",
      absencePenaltyNotifyEmployee: "Notify employee",
      day: "day",
      days: "days"
    },
    themeSettings: {
      title: "Theme Settings",
      description: "Choose your preferred color theme and mode",
      saveChanges: "Save Changes",
      colorTheme: "Color Theme",
      unsavedChange: "Unsaved change",
      current: "Current",
      preview: "Selected Theme Preview",
      primaryButton: "Primary Button",
      secondaryButton: "Secondary Button",
      saved: "Settings saved successfully!"
    },
    locationSettings: {
      title: "Set your company address on the map and then set the area where users can register",
      loading: "Loading...",
      searchLocation: "Search for Location",
      searchPlaceholder: "Search for location (e.g., Cairo, Egypt)",
      search: "Search",
      searching: "Searching...",
      getMyLocation: "Get My Location",
      gettingLocation: "Getting location...",
      retry: "Retry",
      latitude: "Latitude",
      latitudePlaceholder: "Latitude (e.g., 30.0444)",
      longitude: "Longitude",
      longitudePlaceholder: "Longitude (e.g., 31.2357)",
      setLocation: "Set Location",
      selectedCoordinates: "Selected Coordinates",
      radius: "Radius",
      meters: "meters",
      replaceCurrent: "Replace Current Location",
      addNew: "Add New Location",
      deleteSelected: "Delete Selected Location",
      location: "Location",
      updateRadius: "Update Radius (in meters)",
      radiusPlaceholder: "Enter new radius",
      updateZone: "Update Zone",
      delete: "Delete",
      browserNotSupported: "Browser does not support geolocation.",
      permissionDenied: "Access to location was denied. Please enable location access in browser settings.",
      locationUnavailable: "Location information is unavailable. Please try again later.",
      locationTimeout: "Location request timed out. Please try again.",
      locationError: "An error occurred while getting your location.",
      permissionCheckError: "Unable to check location permissions. Please try again.",
      searchRequired: "Please enter a location name to search",
      locationNotFound: "Location not found. Please try another name.",
      searchError: "Error searching for location. Please try again.",
      selectLocationFirst: "Please select a location on the map first",
      selectOrConfirm: "Please select a location on the map or make sure there is a location to replace",
      locationUpdated: "Location updated successfully",
      updateFailed: "Failed to update location",
      updateError: "Error updating location",
      radiusRequired: "Please enter a valid radius value",
      radiusUpdated: "Radius updated successfully",
      radiusUpdateFailed: "Failed to update radius",
      radiusUpdateError: "Error updating radius",
      locationAdded: "New location added successfully",
      locationAddFailed: "Failed to add location",
      locationAddError: "Error adding location",
      deleteConfirm: "Are you sure you want to delete this location?",
      locationDeleted: "Location deleted successfully",
      locationDeleteFailed: "Failed to delete location",
      locationDeleteError: "Error deleting location",
      invalidCoordinates: "Please enter valid latitude and longitude values",
      invalidLatitude: "Latitude must be between -90 and 90",
      invalidLongitude: "Longitude must be between -180 and 180",
      fetchFailed: "Failed to fetch locations",
      selectedLocation: "Selected Location:"
    },
    deductionSettings: {
      title: "Lateness Settings",
      description: "Manage lateness deduction rules for employees",
      rulesCount: "rules defined",
      howItWorks: "How do lateness rules work?",
      howItWorksDesc: "Deduction rules are applied based on lateness duration. For example, if lateness is between 30-60 minutes, 0.5 of the daily salary will be deducted.",
      rules: "Lateness Rules",
      addNewRule: "Add New Rule",
      minMinutes: "Minimum (minutes)",
      maxMinutes: "Maximum (minutes)",
      deductionRate: "Deduction Rate",
      actions: "Actions",
      noRules: "No rules added yet",
      min: "Min",
      max: "Max",
      fractionPlaceholder: "Deduction rate",
      note: "Note: Make sure the minimum is less than the maximum in all rules.",
      refreshData: "Refresh Data",
      saveChanges: "Save Changes",
      editMode: "Edit Mode",
      loading: "Loading...",
      tokenNotFound: "Authentication token not found",
      loadFailed: "Failed to load lateness settings",
      settingsNotFound: "Lateness settings not found",
      loaded: "Lateness settings loaded successfully",
      minMustBeLess: "Minimum must be less than maximum",
      allFieldsRequired: "Please enter all fields (minimum, maximum, deduction)",
      ruleError: "Rule",
      updateFailed: "Failed to update lateness settings",
      saved: "Lateness settings saved successfully",
      enableLateSettings: "Enable lateness settings",
      enableLateSettingsDesc: "Enable or disable the penalty system",
      enabled: "Enabled",
      disabled: "Disabled",
      penaltyRules: "Penalty rules",
      addRule: "Add rule",
      addFirstRule: "Add first rule",
      availablePenaltyTypes: "Available penalty types:",
      autoPenaltyTypes: "Quarter / Half / Full day:",
      autoPenaltyTypesDesc: "Calculated automatically based on working hours — no amount needed",
      customPenaltyType: "Custom:",
      customPenaltyTypeDesc: "You can set a penalty amount, penalty days, or both",
      noRulesDefined: "No rules defined",
      rule: "Rule",
      minute: "minute",
      day: "day",
      delayMinutes: "Delay minutes",
      penaltyType: "Penalty type",
      selectPenaltyType: "Select penalty type",
      none: "None",
      quarterDay: "Quarter day",
      halfDay: "Half day",
      fullDay: "Full day",
      custom: "Custom",
      quarterDayAuto: "Quarter day (auto)",
      halfDayAuto: "Half day (auto)",
      fullDayAuto: "Full day (auto)",
      customSpecify: "Custom (set amount or days)",
      autoCalculatedHint: "This penalty will be calculated automatically based on working hours",
      penaltyAmount: "Penalty amount",
      penaltyDays: "Penalty days",
      optional: "(optional)",
      example15: "e.g. 15",
      example200: "e.g. 200",
      example05: "e.g. 0.5",
      leaveEmptyUseDays: "You can leave this empty if you only use penalty days",
      leaveEmptyUseAmount: "You can leave this empty if you only use the penalty amount"
    },
    autoCheckout: {
      title: "Auto Checkout Settings",
      description: "Control when employees are checked out automatically after their shift ends if they forget to check out",
      loading: "Loading auto checkout settings...",
      refresh: "Refresh",
      edit: "Edit",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      enabled: "Enable auto checkout",
      enabledHint: "When enabled, employees are checked out automatically after a set time past shift end",
      on: "On",
      off: "Off",
      graceHours: "Hours after shift end",
      graceHoursHint: "Hours after work end before auto checkout runs (0 to 24)",
      graceHoursInvalid: "Hours must be between 0 and 24",
      hours: "hours",
      countOvertime: "Count overtime",
      countOvertimeHint: "When enabled, time after shift end can count as overtime on auto checkout",
      notifyEmployee: "Notify employee",
      notifyEmployeeHint: "Send a notification to the employee on auto checkout",
      notifyAdmin: "Notify admin",
      notifyAdminHint: "Send a notification to admins on auto checkout",
      loadFailed: "Failed to load auto checkout settings",
      settingsNotFound: "Auto checkout settings not found",
      saved: "Auto checkout settings saved successfully",
      saveFailed: "Failed to save auto checkout settings",
      deductionTitle: "Missed checkout deduction",
      deductionHint: "Choose whether forgetting to check out deducts from salary, and how much.",
      policyFree: "No deduction",
      policyQuarterDay: "Quarter day",
      policyHalfDay: "Half day",
      policyFixedAmount: "Fixed amount",
      fixedAmount: "Fixed amount",
      fixedAmountHint: "Amount deducted in the payroll currency",
      fixedAmountInvalid: "Enter a positive amount with at most 2 decimals"
    },
    checkInSelfie: {
      title: "Check-in Selfie Settings",
      description: "Company default for selfie at check-in. Branches can inherit this or set their own policy.",
      loading: "Loading check-in selfie settings...",
      refresh: "Refresh",
      edit: "Edit",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      mode: "Selfie mode",
      modeHint: "Choose whether selfie is disabled, optional, or required",
      modes: {
        off: "Off",
        optional: "Optional",
        required: "Required",
      },
      modeHints: {
        off: "Do not ask for a selfie at check-in",
        optional: "Show selfie capture but allow skip",
        required: "Employee must take a selfie to complete check-in",
      },
      applyTo: "Apply to work types",
      applyToHint: "Selfie policy applies only to selected employee work types",
      applyToRequired: "Select at least one work type",
      modeInvalid: "Invalid selfie mode",
      workTypes: {
        office: "Office",
        remote: "Remote",
        hybrid: "Hybrid",
        delivery: "Delivery",
      },
      loadFailed: "Failed to load check-in selfie settings",
      settingsNotFound: "Check-in selfie settings not found",
      saved: "Check-in selfie settings saved successfully",
      saveFailed: "Failed to save check-in selfie settings",
    },
    aiSettings: {
      title: "AI Settings",
      description: "Manage AI model API keys easily and securely",
      setApiKey: "SET API Key",
      newApiKey: "New API Key",
      newApiKeyPlaceholder: "Enter new API key",
      createApiKey: "Create API Key",
      noApiKeys: "No API keys available",
      fetchFailed: "Failed to fetch API keys",
      fetchError: "Error fetching API keys",
      onlyOneKey: "You can only create one API key",
      createFailed: "Failed to create API key",
      createError: "Error creating API key",
      updateFailed: "Failed to update API key",
      updateError: "Error updating API key",
      deleteFailed: "Failed to delete API key",
      deleteError: "Error deleting API key",
      loading: "Loading..."
    },
    holidaysSettings: {
      title: "Official Holidays",
      description: "Manage and configure official holidays",
      fetchFailed: "Failed to load holidays",
      updateSuccess: "Holiday updated successfully",
      updateFailed: "Failed to update holiday",
      deleteConfirm: "Are you sure you want to delete this holiday?",
      deleteSuccess: "Holiday deleted successfully",
      deleteFailed: "Failed to delete holiday"
    },
    createdBy: "Created by",
    created: "Created",
    lastUpdated: "Last updated",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    create: "Create",
    createDepartment: "Create Department",
    newDepartmentName: "New Department Name",
    departmentBadge: "Department"
  },
  languageSelector: {
    title: "Language Settings",
    description: "Choose interface language",
    arabic: "العربية",
    english: "English",
    save: "Save Changes",
    unsaved: "Unsaved change",
    german: "Deutsch"
  },
  dashboard: {
    title: "Dashboard",
    description: "Overview of your information",
    seeMore: "See more",
    seeLess: "See less",
    tabs: {
      employee: "For Employee",
      profile: "My Profile",
      manager: "For Manager"
    },
    errors: {
      tokenNotFound: "Token not found",
      fetchFailed: "Failed to fetch dashboard data. Please try again later.",
      retry: "Retry"
    },
    loading: {
      loadingData: "Loading data...",
      loadingPayroll: "Loading payroll data...",
      loadingEmployees: "Loading employees data..."
    },
    leaveBalance: {
      sickLeave: "Sick Leave",
      annualLeave: "Annual Leave",
      workFromHome: "Work from Home",
      days: "days",
      used: "Used",
      total: "Total",
      thisMonth: "This month"
    },
    empty: {
      noDashboardTitle: "No data available",
      noDashboardMessage: "No dashboard data is available currently. Please try again later or contact administration.",
      noEmployeeTitle: "No data",
      noEmployeeMessage: "No employee data available to display."
    },
    roles: {
      admin: "Admin",
      manager: "Manager",
      employee: "Employee",
      hr: "HR",
      accountant: "Accountant"
    },
    employeeInfo: {
      directManager: "Direct Manager",
      department: "Department",
      branch: "Branch",
      workLocation: "Current work location"
    },
    manager: {
      totalEmployees: "Total Employees",
      workFromHome: "Work from Home",
      outsideOffice: "Outside Office",
      pendingRequests: "Pending Employees Need Approval",
      pendingRequestsCount: "pending employee ",
      expiringDocuments: "Reminder: Documents About to Expire",
      expiringDocumentsDesc: "Documents that will expire within 30 days",
      expiresIn: "Expires on",
      days: "days",
      quickShortcuts: "Pending Requests",
      quickShortcutsDesc: "Requests that need review",
      underReview: "Under Review",
      viewAllRequests: "View All Requests",
      noPendingRequests: "No pending requests",
      contract: "Work Contract",
      visa: "Work Visa",
      present: "Present on time",
      late: "Late",
      absent: "Absent",
      attendanceRate: "Attendance Rate",
      totalAttendance: "Total Attendance",
      activeEmployees: "Active Employees",
      pendingEmployees: "Pending Employees",
      inactiveEmployees: "Inactive Employees",
      seeMore: "See More",
      seeLess: "See Less",
      cardDetailsEmpty: "No employees in this category",
      cardDetailsLoading: "Loading data...",
      cardDetailsClose: "Close",
      cardDetailsName: "Name",
      cardDetailsDepartment: "Department",
      cardDetailsPosition: "Position",
      cardDetailsStatus: "Status",
      cardDetailsCheckIn: "Check-in time",
      cardDetailsEmail: "Email",
      cardDetailsPhone: "Phone",
      cardDetailsCount: "{count} employees",
      cardDetailsLeaveCount: "{count} requests",
      cardDetailsLeaveEmpty: "No pending leave requests",
      cardDetailsLeaveType: "Leave type",
      cardDetailsLeaveDates: "Period",
      cardDetailsLeaveDays: "Days",
      viewPendingLeavesPage: "Pending requests page",
      pendingLeavesViewOnlyHint: "View only — no approve or reject here. Click a row or the button to open the pending leave requests page.",
      leaveCategory: {
        annual: "Annual",
        sick: "Sick",
        workFromHome: "Work from home",
        emergency: "Emergency",
        maternity: "Maternity"
      },
      inactiveEmployeesHint: "This list shows inactive employees only: disabled employees, those marked as «under training», and any status other than «active».",
      help: {
        totalEmployees: "Total number of employees registered in the system, regardless of status or today’s attendance.",
        workFromHome: "Remote employees who checked in today.",
        outsideOffice: "Hybrid and delivery employees who checked in today from outside the office zone.",
        pendingRequests: "Leave requests still waiting for manager or admin approval.",
        present: "Employees who checked in on time today (within the grace period).",
        late: "Employees who checked in today after the grace period ended.",
        absent: "Employees with no check-in today who are counted as absent.",
        attendanceRate: "Share of present and late employees today out of all active employees.",
        totalAttendance: "Sum of today’s attendance records (present + late + absent).",
        activeEmployees: "Employees whose status is «active» in the system, regardless of today’s check-in.",
        pendingEmployees: "Employees waiting for join approval or account activation.",
        inactiveEmployees: "Inactive employees: disabled, «under training», or any status other than «active»."
      }
    },
    profile: {
      editMyInfo: "Edit My Information",
      edit: "Edit Profile",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      updateSuccess: "Profile updated successfully",
      email: "Email",
      emailPlaceholder: "Enter email",
      phone: "Phone Number",
      phonePlaceholder: "Enter phone number",
      employeeId: "Employee ID",
      joinDate: "Join Date",
      directManager: "Direct Manager",
      department: "Department",
      workLocation: "Work Location",
      additionalInfo: "Additional Information",
      address: "Address",
      addressPlaceholder: "Enter address",
      nationalID: "National ID",
      gender: "Gender",
      education: "Education",
      educationPlaceholder: "Enter education",
      skills: "Skills",
      accountStatus: "Account Status",
      active: "Active",
      attendanceSummary: "Attendance summary",
      totalDeductions: "Total deductions",
      allowances: "Allowances",
      allowancesAssigned: "Allowances assigned to you",
      emergencyContact: "Emergency Contact",
      relation: "Relation",
      bank: "Bank",
      errors: {
        noEmployeeId: "Employee ID not found",
        noChanges: "No changes to save",
        updateFailed: "Failed to update profile"
      }
    },
    common: {
      notSpecified: "Not specified",
      unknown: "Unknown",
      document: "Document",
      leave: "Leave"
    },
    activeEmployees: {
      title: "Active Employees",
      description: "Employees checked in today",
      presentCount: "{count} present on time",
      remoteCount: "{count} remote",
      attendanceRate: "{percent}% attendance rate",
      loading: "Loading employees...",
      fetchError: "An error occurred while fetching active employees",
      empty: "No active employees at the moment",
      arrivalTime: "Arrival time:",
      location: "Location:",
      call: "Call",
      email: "Email",
      sendEmail: "Send email",
      viewFullDetails: "View full details",
      emailLabel: "Email",
      phoneLabel: "Phone number",
      locationLabel: "Location",
      baseSalary: "Base salary",
      joinDate: "Join date",
      status: "Status",
      unavailable: "Unavailable",
      detailsLoadFailed: "Failed to load details",
      noEmail: "No email address",
      noPhone: "No phone number",
      mainOffice: "Main office",
      present: "Present on time",
      inMeeting: "In a meeting",
      remote: "Remote",
      active: "Active"
    },
    lastRequests: {
      title: "Recent Requests",
      description: "View all your leave requests",
      all: "All",
      pending: "Pending review",
      approved: "Approved",
      rejected: "Rejected",
      newest: "Newest",
      oldest: "Oldest",
      noMatching: "No requests match the filter",
      reason: "Reason:",
      rejectionReason: "Rejection reason:",
      date: "Date:",
      duration: "Duration:",
      status: "Status:",
      statsTitle: "Request statistics",
      statsDescription: "Overview of leave request statuses",
      employeeIdMissing: "Employee ID is not available",
      noRequests: "No requests available",
      fetchError: "An error occurred while loading requests",
      unknown: "Unknown",
      chartCount: "Count"
    },
    notifications: {
      title: "Notifications",
      unreadSummary: "You have {unread} unread notification(s) out of {total}",
      markAllRead: "Mark all as read",
      deleteAll: "Delete all",
      decreasePerPageAria: "Decrease notifications per page",
      increasePerPageAria: "Increase notifications per page",
      perPageAria: "Notifications per page",
      perPageLabel: "Notifications per page",
      loading: "Loading notifications...",
      noNotifications: "No notifications",
      notificationAria: "Notification: {title}",
      untitled: "Untitled notification",
      unreadAria: "Unread notification",
      noMessage: "No notification message",
      loadMoreAria: "Load more notifications",
      loadMore: "Show more notifications",
      fetchFailed: "Failed to fetch notifications",
      fetchError: "An error occurred while fetching notifications",
      markReadFailed: "Failed to update notification status",
      markAllReadFailed: "Failed to update notifications status",
      deleteFailed: "Failed to delete notification",
      deleteAllFailed: "Failed to delete all notifications"
    }
  },
  employees: {
    title: "Employee Management",
    description: "Manage employee information",
    list: "Employees List",
    manageAndView: "Manage and view employee data",
    searchPlaceholder: "Search for employee...",
    filter: {
      all: "All",
      admins: "Admins",
      teamLeaders: "Team Leaders",
      employees: "Employees"
    },
    noEmployees: "No employees",
    noSearchResults: "No search results",
    addFirstEmployee: "Add first employee",
    unassignedShifts: {
      urgent: "Urgent — action required",
      bannerOne: "1 employee has no shift — assign now",
      bannerMany: "{count} employees have no shift — assign now",
      bannerHint: "Without a shift, reminders and absence tracking may be inaccurate. Tap to assign immediately.",
      viewCta: "Assign now",
      bannerCta: "Assign now",
      panelTitle: "Employees without a shift",
      panelHint: "Tap an employee to assign a shift. This is required for accurate attendance and payroll.",
      branchLabel: "Branch: {branch}",
      noBranch: "No branch",
      missingShift: "No shift",
      backToList: "← Back to list",
      assignHint: "Choose the appropriate shift, then save. Assignment is admin / team leader only.",
      assignCta: "Assign shift",
      selectShiftRequired: "Select a shift before saving",
      assignSuccess: "Shift assigned successfully",
      assignFailed: "Failed to assign shift",
      loadShiftsFailed: "Failed to load shifts",
      noShiftsAvailable: "No shifts available. Add shifts in company or branch settings first."
    },
    pendingApproval: "No employees pending approval",
    pendingApprovalTitle: "Employees Pending Approval",
    pendingApprovalDescription: "They need management approval to join the system",
    loading: "Loading...",
    updating: "Updating...",
    updateList: "Update List",
    hide: "Hide",
    show: "Show",
    noDataForMonth: "No employee data available for",
    sortOptions: {
      none: "No Sort",
      topSalary: "Highest Salary",
      topBonuses: "Highest Bonuses",
      topDeductions: "Highest Deductions",
      oldestStartDate: "Oldest Start Date"
    },
    perPage: "per page",
    pageOf: "Page",
    of: "of",
    previous: "Previous",
    next: "Next",
    enterPerPage: "Enter number per page",
    apply: "Apply",
    invalidNumber: "Please enter a valid number greater than 0",
    noMatchSearch: "No employees found matching",
    tryModifySearch: "Try modifying the search term",
    startAddingEmployee: "Start by adding the first employee to the system to manage employees and track salaries.",
    confirmDeleteEmployee: "Are you sure you want to delete employee: {name}?\n\nThis action cannot be undone.",
    deleteEmployeeSuccess: "Employee deleted successfully",
    deleteEmployeeError: "An error occurred while deleting the employee",
    fetchEmployeesError: "An error occurred while fetching employees",
    promoteSuccess: "Employee promoted to admin successfully",
    promoteError: "An error occurred while promoting the employee to admin",
    demoteSuccess: "Employee demoted to regular employee successfully",
    demoteError: "An error occurred while demoting the employee",
    promoteToAdmin: "Promote to admin",
    demoteToEmployee: "Demote to employee",
    deleteEmployeeTitle: "Delete employee",
    showEmployeeNumber: "Show employee number",
    employeeFallback: "Employee",
    whatsappCustomPlaceholder: "Leave empty to use the default template or write a custom message...",
    employeeInfo: "Employee information",
    selectEmployeeAndMessage: "Please select an employee and write a message",
    whatsapp: {
      messageType: "Message type",
      salarySlip: "Salary slip",
      leaveBalance: "Leave balance",
      notification: "Notification",
      customMessage: "Custom message",
      templatePreview: "Template preview",
      editMessageOptional: "Edit message (optional)",
      nameLabel: "Name:",
      phoneLabel: "Phone:",
      positionLabel: "Position:",
      departmentLabel: "Department:",
      sending: "Sending message...",
      sendFailed: "Failed to send message",
      reasonPrefix: "Reason:",
      sendingBtn: "Sending...",
      sentSuccess: "Sent successfully",
      sendMessage: "Send message",
      to: "To:",
      chooseMessageType: "Choose message type",
      salarySlipDesc: "Send monthly salary details",
      leaveBalanceDesc: "Send available leave update",
      notificationTitle: "Important notice",
      notificationDesc: "Send a general notice or alert",
      customMessageDesc: "Write a free custom message",
      templateSalary: "📊 *Salary slip for {month}*\nDear: *{name}*\nPosition: {position}\nDepartment: {department}\n💰 *Salary details:*\n• Base salary: {baseSalary}\n• Allowances: {allowances}\n• Deductions: {deductions}\n💸 *Total salary: {netSalary}*\nDue date: {date}\nThank you for your efforts 🙏",
      templateLeave: "🏖️ *Leave balance update*\nDear: *{name}*\n📊 *Leave balance details:*\n• Available balance: {leaveBalance} days\n• Days used: {usedDays} days\n• Total annual days: 30 days\nUpdated: {date}\nFor leave requests, please contact HR.\nHave a great leave! 🌟",
      templateNotification: "🔔 *Important notice*\nDear: *{name}*\nPosition: {position}\nDepartment: {department}\nPlease note that...[write the notice here]\nDate: {date}\nThank you for your attention.",
      templateCustom: "Hello\nDear: *{name}*\n[Write your message here]\nBest regards, HR  {date}"
    },
    directoryTitle: "Employees Directory",
    directoryDescription: "Browse contact information and professional details of your colleagues",
    totalEmployees: "Total employees",
    activeEmployees: "Active employees",
    teamLeaders: "Team leaders",
    departmentsCount: "Departments",
    displayedResults: "Displayed results",
    ofEmployees: "of {total} employees",
    resultsCount: "({count} results)",
    searchDirectoryPlaceholder: "Search by name, department, skills...",
    allStatuses: "All statuses",
    departmentLabel: "Department:",
    emailLabel: "Email:",
    phoneLabel: "Phone:",
    locationLabel: "Location:",
    hireDateLabel: "Hire date:",
    educationLabel: "Education:",
    skillsLabel: "Skills:",
    projectsLabel: "Projects:",
    yearsExperience: "{n} years experience",
    moreSkills: "+{n} more",
    noResultsTitle: "No results",
    noResultsHint: "No employees match the current search. Try changing the search term or filter.",
    rejectEmployeeTitle: "Reject employee",
    rejectEmployeeHint: "Please enter the rejection reason for {name}",
    rejectReasonPlaceholder: "Rejection reason (10–500 characters)...",
    confirmReject: "Confirm rejection",
    privacyNoteTitle: "Privacy note",
    privacyNoteBody: "The employees directory shows contact information and basic professional data. Sensitive financial information is protected according to privacy policies.",
    unauthorizedPage: "You are not authorized to use this page",
    adminCheckFailed: "Could not verify admin permissions",
    rejectFailed: "Failed to reject employee",
    rejectError: "An error occurred while rejecting the employee",
    tableHeaders: {
      employeeId: "Employee ID",
      name: "Name",
      email: "Email",
      position: "Position",
      department: "Department",
      branch: "Branch",
      actions: "Actions"
    },
    details: {
      loading: "Loading employee data...",
      notFound: "Employee data not found",
      notFoundDesc: "Please check the employee ID or try again",
      backToEmployees: "Back to Employees List",
      overview: "Overview",
      notSpecified: "Not specified",
      teamLeader: "Team Leader",
      edit: "Edit",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      quickActions: "Quick Actions",
      quickActionsMenu: "Quick Actions Menu",
      email: "Email",
      phone: "Phone",
      workLocation: "Work Location",
      role: "Role",
      joinDate: "Join Date",
      contractDurationMonths: "Contract Duration",
      contractMonthsUnit: "{count} months",
      contractStartDate: "Contract Start",
      contractEndDate: "Contract End",
      renewContract: "Renew contract",
      renewResidence: "Renew residence",
      renewContractTitle: "Renew employee contract",
      renewResidenceTitle: "Renew residence period",
      renewDurationMonths: "New duration (months)",
      renewDurationPlaceholder: "e.g. 12",
      renewDurationHint: "The new end date will be calculated from today using the months you enter.",
      renewConfirm: "Confirm renewal",
      renewing: "Renewing...",
      contractRenewed: "Contract renewed successfully",
      residenceRenewed: "Residence period renewed successfully",
      renewFailed: "Renewal failed. Please try again.",
      directManager: "Direct Manager",
      approvalStatus: "Approval Status",
      status: "Status",
      approved: "Approved",
      rejected: "Rejected",
      pending: "Pending",
      notSet: "Not set",
      active: "Active",
      inactive: "Inactive",
      onLeave: "On Leave",
      underTraining: "Under Training",
      serviceEnded: "Service Ended",
      assignTeamLeader: "Assign Team Leader",
      editTeamLeader: "Edit Team Leader",
      employeeRole: "Employee",
      assignPermissionApprover: "Assign Permission Approver",
      permissionApproverHint: "Choose who receives this employee's permission requests. Default: department team leader if available, otherwise admin.",
      useDefaultApprover: "Default (team leader / admin)",
      addBonus: "Add Bonus",
      addDeduction: "Add Deduction",
      convertToEmployee: "Convert to Regular Employee",
      converting: "Converting...",
      addAllowance: "Add Allowance",
      addTax: "Add Tax",
      addInsurance: "Add Insurance",
      errors: {
        fetchFailed: "Failed to fetch employee data",
        fetchError: "An error occurred while fetching employee data",
        fetchDepartmentsFailed: "Failed to fetch departments",
        fetchDepartmentsError: "An error occurred while fetching departments",
        selectDepartment: "Please select at least one department",
        noUserId: "No user ID found for employee",
        assignFailed: "Failed to assign team leader",
        assignError: "An error occurred while assigning team leader",
        convertFailed: "Failed to convert team leader",
        convertError: "An error occurred while converting team leader",
        taxNameRequired: "Please enter deduction name",
        taxAmountRequired: "Please enter a valid amount greater than zero",
        taxAddFailed: "Failed to add tax",
        taxAddError: "An error occurred while adding tax",
        insuranceNameRequired: "Please enter deduction name",
        insuranceAmountRequired: "Please enter a valid amount greater than zero",
        insuranceAddFailed: "Failed to add insurance",
        insuranceAddError: "An error occurred while adding insurance",
        bonusFieldsRequired: "Please fill all required fields",
        bonusAmountRequired: "Amount must be greater than zero",
        bonusAddFailed: "Failed to add bonus",
        bonusAddError: "An error occurred while adding bonus",
        bonusDeleteConfirm: "Are you sure you want to delete this bonus? This action cannot be undone.",
        bonusDeleteFailed: "Failed to delete bonus",
        bonusDeleteError: "An error occurred while deleting bonus",
        deductionFieldsRequired: "Please fill all required fields",
        deductionAmountRequired: "Amount must be greater than zero",
        deductionAddFailed: "Failed to add deduction",
        deductionAddError: "An error occurred while adding deduction",
        deductionDeleteConfirm: "Are you sure you want to delete this deduction? This action cannot be undone.",
        deductionDeleteFailed: "Failed to delete deduction",
        deductionDeleteError: "An error occurred while deleting deduction",
        allowanceFieldsRequired: "Please fill all required fields",
        allowanceEndDateRequired: "Please enter end date or enable \"Permanent\" option",
        allowanceSaveFailed: "An error occurred while saving allowance",
        allowanceIdInvalid: "Invalid allowance ID",
        bonusIdInvalid: "Invalid bonus ID",
        deductionIdInvalid: "Invalid deduction ID",
        convertToAdminFailed: "Failed to convert employee to admin",
        permissionApproverLoadFailed: "Failed to load permission approver settings",
        permissionApproverUpdateFailed: "Failed to update permission approver",
        updateFailed: "Update failed",
        allowanceDeleteConfirm: "Are you sure you want to delete this allowance? This action cannot be undone."
      },
      success: {
        permissionApproverUpdated: "Permission approver updated",
        convertToAdmin: "Employee converted to admin successfully",
        assignTeamLeader: "Employee assigned as team leader successfully",
        convertToEmployee: "Team leader converted to regular employee successfully",
        taxAdded: "Tax added successfully",
        insuranceAdded: "Insurance added successfully",
        bonusAdded: "Bonus added successfully",
        bonusDeleted: "Bonus deleted successfully",
        deductionAdded: "Deduction added successfully",
        deductionDeleted: "Deduction deleted successfully",
        allowanceUpdated: "Allowance updated successfully",
        allowanceAdded: "Allowance added successfully"
      },
      confirmations: {
        convertRoleToEmployee: "Are you sure you want to convert {role} to an employee?",
        convertToAdmin: "Are you sure you want to convert this employee to admin?",
        convertToEmployee: "Are you sure you want to convert team leader to regular employee?"
      },
      deductions: {
        socialInsurance: "Social Insurance",
        insurance: "Insurance",
        tax: "Tax",
        taxes: "Taxes",
        loans: "Loans",
        absence: "Absence"
      },
      fullDetails: "Full Employee Details",
      personalInfo: "Personal Information",
      jobInfo: "Job Information",
      datesAndContact: "Dates & Contact",
      employeeNumber: "Employee Number",
      nationalID: "National ID",
      gender: "Gender",
      male: "Male",
      female: "Female",
      birthDate: "Date of Birth",
      maritalStatus: "Marital Status",
      address: "Address",
      emergencyContact: "Emergency Contact",
      department: "Department",
      position: "Position",
      yes: "Yes",
      no: "No",
      periodOfStay: "Period of Stay (days)",
      startDate: "Start Date",
      endDate: "End Date",
      contractDurationMonths: "Contract Duration",
      contractMonthsUnit: "{count} months",
      residenceExpiry: "Residence Expiry",
      leaveBalance: "Leave Balance",
      sickLeaveLabel: "Sick Leave",
      annualLeaveLabel: "Annual Leave",
      unpaidLeaveLabel: "Unpaid Leave",
      workFromHomeLabel: "Work from Home",
      monthlyWorkFromHomeLabel: "Work from Home (Monthly)",
      yearLabel: "Year",
      day: "day",
      days: "days",
      createdAt: "Created At",
      experience: "Experience",
      documents: "Documents",
      documentN: "Document",
      size: "Size",
      type: "Type",
      viewDocument: "View Document",
      customAllowances: "Custom Allowances",
      amount: "Amount",
      permanent: "Permanent",
      delete: "Delete",
      deleting: "Deleting...",
      accountInfo: "Account Information",
      lastDevice: "Last Device",
      brand: "Brand",
      deviceTypeLabel: "Device Type",
      system: "System",
      deviceRequests: "Device Requests",
      requestsCount: "requests",
      loadingDeviceRequests: "Loading device requests...",
      noDeviceRequests: "No device requests at the moment",
      unknown: "Unknown",
      pendingReview: "Under Review",
      deviceBrand: "Brand",
      deviceName: "Device Name",
      deviceModel: "Model",
      deviceId: "Device ID",
      operatingSystem: "Operating System",
      carrier: "Carrier",
      requestDate: "Request Date",
      approve: "Approve",
      reject: "Reject",
      unavailable: "Unavailable",
      minutes: "minutes",
      hours: "hours",
      minute: "minute",
      hour: "hour",
      convertToAdmin: "Convert to Admin",
      convertingToAdmin: "Converting...",
      allowanceDeleted: "Allowance deleted successfully",
      employeeUpdated: "Employee data updated successfully",
      rejectReasonPrompt: "Please enter rejection reason:",
      userEmail: "User Email",
      noAllowances: "No allowances",
      unselectAll: "Unselect All",
      assigning: "Assigning...",
      editAllowance: "Edit Allowance",
      addNewAllowance: "Add New Allowance",
      addInsuranceForEmployee: "Add Insurance for Employee",
      editEmployees: "Edit Employees",
      editDepartments: "Edit Departments",
      editBranches: "Edit Branches",
      editTasks: "Edit Tasks",
      editClients: "Edit Clients",
      editCategories: "Edit Categories",
      editEquipment: "Edit Assets",
      editTools: "Edit Tools",
      editPositions: "Edit Positions",
      permissionLabels: {
        salaryFinance: "Salary & Finance",
        salary: "Salary",
        payroll: "Payroll",
        transactions: "Financial Transactions",
        employees: "Employees",
        viewEmployees: "View Employees",
        createEmployees: "Add Employees",
        deleteEmployees: "Delete Employees",
        approveEmployees: "Approve Employees",
        attendanceGroup: "Attendance",
        checkIn: "Check In",
        attendance: "Attendance",
        dailyAttendance: "Daily Attendance",
        departmentsBranches: "Departments & Branches",
        viewDepartments: "View Departments",
        viewBranches: "View Branches",
        tasks: "Tasks",
        viewTasks: "View Tasks",
        createTasks: "Create Tasks",
        deleteTasks: "Delete Tasks",
        leaveRequests: "Leave Requests",
        viewLeaveRequests: "View Leave Requests",
        leaveApprove: "Approve Leave",
        permissionRequests: "Permission Requests",
        viewPermissionRequests: "View Permission Requests",
        permissionApprove: "Approve Permissions",
        reports: "Reports",
        viewReports: "View Reports",
        salaryReports: "Salary Reports",
        attendanceReports: "Attendance Reports",
        other: "Other",
        clients: "Clients",
        categories: "Categories",
        equipment: "Assets",
        tools: "Tools",
        positions: "Positions",
        notifications: "Notifications",
        dashboard: "Dashboard",
        devices: "Devices"
      },
      adminRole: "Admin",
      thisUser: "this user",
      assign: "Assign",
      add: "Add",
      update: "Update",
      selectAll: "Select All",
      permissionsLabel: "Permissions",
      departmentsMultiSelect: "Departments (multiple selection allowed)",
      noDepartmentsAvailable: "No departments available",
      departmentsSelected: "{count} department(s) selected",
      addBonusForEmployee: "Add Bonus for Employee",
      addDeductionForEmployee: "Add Deduction for Employee",
      addTaxForEmployee: "Add Tax for Employee",
      currentMonth: "Current month",
      permanentAllowance: "Permanent allowance",
      approverLabel: "Approver",
      currentDefaultApprover: "Current default approver",
      customApprover: "Custom",
      teamLeaderOrAdmin: "Team leader / Admin",
      dailyOrders: "Daily Orders Count",
      dailyOrdersPlaceholder: "Enter daily orders count",
      dailyOrdersHint: "Please enter the expected daily orders count for the employee",
      contactInfo: "Contact Information",
      employmentInfo: "Employment Information",
      fullName: "Full Name",
      basicSalary: "Basic Salary",
      branch: "Branch",
      probationStatus: "Probation Status",
      selectPosition: "Select position...",
      selectDepartment: "Select department...",
      selectBranch: "Select branch...",
      shift: "Shift",
      selectShift: "Select shift...",
      noShift: "No shift (branch/org hours)",
      branchChangedShiftWarning: "Branch changed — select a shift for the new branch before saving (schedule and location follow the new branch).",
      branchChangedShiftRequired: "Select a shift for the new branch before saving",
      selectStatus: "Select status",
      editData: "Edit Data",
      saveChanges: "Save Changes",
      loadingEmployee: "Loading employee data...",
      ended: "Ended",
      notActivated: "Not Activated",
      noChangesToSave: "No changes to save",
      saveDataError: "An error occurred while saving data",
      allowanceSaveError: "An error occurred while saving allowance",
      modals: {
        allowanceName: "Allowance name",
        allowanceNamePlaceholder: "e.g. Transportation allowance",
        amountPlaceholder: "e.g. 500",
        bonusType: "Bonus type",
        bonusTypePerformance: "Performance bonus",
        bonusTypeAnnual: "Annual bonus",
        bonusTypeProject: "Project bonus",
        bonusTypeOther: "Other",
        bonusDescriptionPlaceholder: "e.g. Performance bonus for excellent work",
        deductionType: "Deduction type",
        deductionAmountPlaceholder: "e.g. 200",
        deductionDescriptionPlaceholder: "e.g. Monthly loan repayment",
        taxName: "Deduction name",
        taxNamePlaceholder: "e.g. Taxes",
        taxAmountPlaceholder: "e.g. 200",
        endDateOptionalPermanent: "End date (leave empty for permanent deduction)",
        insuranceNamePlaceholder: "e.g. Social insurance",
        insuranceAmountPlaceholder: "e.g. 300",
        description: "Description",
        deductionTypeLoan: "Loan deduction",
        deductionTypeAdvance: "Advance deduction",
        deductionTypeAbsence: "Absence deduction",
        deductionTypeLate: "Late deduction"
      },
      attendanceHeatmap: {
        title: "Monthly Attendance Map",
        subtitle: "See each day's status at a glance — tap a day for details",
        prevMonth: "Previous month",
        nextMonth: "Next month",
        loading: "Loading attendance map...",
        loadError: "Failed to load attendance data",
        noRecord: "No record",
        futureDay: "Upcoming day",
        legend: "Legend",
        selectedDay: "Selected day",
        presentShort: "On time",
        lateShort: "Late",
        absentShort: "Absent",
        leaveShort: "Leave",
        unpaidLeaveShort: "Unpaid",
        holidayShort: "Holiday",
        earlyLeaveShort: "Early out",
        halfDayShort: "Half day",
        weekendShort: "Weekend",
        emptyShort: "—",
        futureShort: "—",
        pickMonth: "Choose month",
        prevYear: "Previous year",
        nextYear: "Next year",
        thisMonth: "This month"
      }
    },
    quickStats: {
      attendanceDetails: "Attendance Details",
      attendanceStats: "Employee attendance statistics",
      attendance: "Attendance",
      coreStats: "Core Statistics",
      workDays: "Work Days",
      presentDays: "Present Days",
      absentDays: "Absent Days",
      leaves: "Leaves",
      holidayDays: "Holiday Days",
      secondaryStats: "Additional Statistics",
      late: "Late",
      earlyLeave: "Early Leave",
      halfDay: "Half Day",
      externalTasks: "External Tasks",
      hoursStats: "Hours Statistics",
      totalHours: "Total Hours",
      overtimeHours: "Overtime Hours",
      hoursUnit: "hrs",
      viewDetails: "View Details",
      viewAttendanceLog: "View attendance log details",
      attendanceLogDetails: "Attendance Log Details",
      closeModal: "Close modal",
      searchPlaceholder: "Search by date or notes...",
      searchAria: "Search attendance log",
      exportCsv: "Export as CSV",
      exportCsvAria: "Export data as CSV file",
      date: "Date",
      delayHours: "Delay Hours",
      deductionAmount: "Deduction Amount",
      overtimeAmount: "Overtime Amount",
      notes: "Notes",
      noRecords: "No records"
    },
    workTime: {
      title: "Work Schedule",
      checkInTime: "Check-in time:",
      checkOutTime: "Check-out time:",
      dailyHours: "Daily working hours:",
      weeklyDays: "Weekly work days:",
      noWorkDays: "No days specified",
      settingsNotFound: "Work settings not found",
      loadError: "An error occurred while loading work settings",
      hoursCount: "{hours} hours",
      hoursAndMinutes: "{hours} hours and {minutes} minutes",
      defaultHours: "8 hours"
    },
    documentsPage: {
      manageTitle: "Manage Documents",
      employeeIdMissing: "Employee ID is not available",
      fetchFailed: "Failed to fetch documents",
      fetchAfterDeleteFailed: "Failed to fetch documents after delete",
      deleteFailed: "Failed to delete document",
      noDescription: "No description",
      error: "Error",
      noDocuments: "No documents",
      noDocumentsForEmployee: "No documents available for this employee",
      unknown: "Unknown",
      viewDocument: "View document",
      deleteDocument: "Delete document",
      newest: "Newest",
      oldest: "Oldest",
      sizeLargest: "File size (largest)"
    },
    deviceRequests: {
      noRequestsHint: "No device requests have been submitted yet",
      noDevicesTitle: "No registered devices",
      noDevicesHint: "No devices are currently registered for this employee",
      manufacturer: "Manufacturer",
      reviewDate: "Review Date",
      rejectionReason: "Rejection Reason"
    },
    detailsHolidays: {
      loading: "Loading requests...",
      duration: "Duration",
      description: "Description",
      filterPendingRejectedApproved: "Pending, Rejected, Approved",
      filterRejectedApprovedPending: "Rejected, Approved, Pending"
    },
    pending: {
      processing: "Processing...",
      approveTitle: "Approve employee",
      rejectTitle: "Reject employee",
      viewDetailsTitle: "View details",
      rejectReasonPrompt: "Rejection reason (optional):",
      noReasonProvided: "No reason provided",
      employeeRole: "Employee",
      generalDepartment: "General department",
      loadingDetails: "Loading details...",
      loadDetailsFailed: "Failed to load employee details",
      tryAgain: "Please try again",
      educationAndSkills: "Education & Skills",
      education: "Education",
      skills: "Skills",
      username: "Username",
      maxDevices: "Max devices",
      registeredDevices: "Registered devices",
      salaryToBeSetByManager: "Will be set by the manager",
      accountStatus: "Account status",
      approvedCheck: "✓ Approved",
      pendingWait: "⏳ Pending",
      rejectedCross: "✗ Rejected",
      financialSetup: "Employee financial setup",
      shiftHint: "Choose the employee shift. Only admin or team leader can assign it.",
      selectWorkLocationOption: "Select work location",
      workLocationHint: "Please select the employee's work location",
      baseSalaryPlaceholder: "Enter base salary",
      baseSalaryHint: "Please enter the employee's base salary",
      contractDurationMonths: "Contract duration (months)",
      contractDurationPlaceholder: "e.g. 12",
      contractDurationHint: "Required. Enter the contract length in months (whole number greater than 0).",
      addNewAllowance: "Add new allowance",
      allowanceName: "Allowance name",
      allowanceNamePlaceholder: "e.g. Transport, Housing, Food allowance",
      allowanceAmount: "Allowance amount",
      allowanceAmountPlaceholder: "Enter allowance amount",
      permanentAllowanceHint: "The allowance will remain with the employee with no end date",
      addToList: "Add to list",
      addedAllowances: "Added allowances ({count})",
      amountPrefix: "Amount: {amount}",
      fromDate: "From {date}",
      fromToDate: "From {start} to {end}",
      noAllowancesYet: "No allowances added yet",
      useFormAbove: "Use the form above to add an allowance",
      saveData: "Save data",
      allowanceCount: "({count} allowance)",
      allowances: "Allowances",
      salaryAndAllowancesSuccess: "Salary set and {count} allowance(s) added successfully",
      salaryPartialAllowances: "Salary saved and {success} allowance(s) added, but {fail} failed",
      salaryAllowancesAllFailed: "Salary saved but all allowances failed to add",
      salarySavedAllowancesFailed: "Salary saved but some allowances failed to add",
      salarySetSuccess: "Salary set successfully",
      saveFailed: "Failed to save data",
      documentN: "Document {n}",
      sizeLabel: "Size: {size}",
      typeLabel: "Type: {type}",
      leaveDaysCount: "{count} day(s)"
    }
  },
  holidays: {
    title: "Holidays",
    description: "Manage and prepare leave reports",
    leaveDays: "Leave Days",
    remainingDays: "Remaining Days",
    sickLeave: "Sick Leave",
    usedDays: "Used Days",
    workFromHome: "Work from Home",
    upcomingLeave: "Upcoming Leave",
    noLeaveRequests: "No leave requests found",
    overview: "Overview",
    past: "Past Leaves",
    requests: "Leave Requests",
    testRequest: "Test Request",
    loading: "Loading...",
    employeeInfo: "Employee Information",
    name: "Name",
    department: "Department",
    position: "Position",
    tableHeaders: {
      leaveType: "Leave Type",
      startDate: "Start Date",
      endDate: "End Date",
      totalDays: "Total Days",
      submissionTime: "Submission Time",
      status: "Status",
      employeeNotes: "Employee Notes",
      managerNotes: "Manager Notes",
      approvedBy: "Approved By"
    },
    status: {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected"
    },
    annualLeave: "Annual Leave",
    remaining: "Remaining",
    used: "Used",
    day: "day",
    days: "days",
    sickLeaveTitle: "Sick Leave",
    workFromHomeTitle: "Work from Home",
    unpaidLeave: "Unpaid Leave",
    of: "of",
    filterByStatus: "Filter by status:",
    all: "All",
    cancelled: "Cancelled",
    previous: "Previous",
    next: "Next",
    page: "Page",
    request: "request",
    pendingRequestsTitle: "Pending Requests",
    pendingRequestsMessage: "You have {count} pending leave request(s)",
    recentRequests: "Recent Requests",
    leaveRequests: "Leave Requests",
    myRequests: "My Requests",
    newRequest: "New Leave Request",
    hideForm: "Hide Form",
    reason: "Reason",
    reasonPlaceholder: "Enter leave reason",
    leaveCategory: "Leave Type",
    selectLeaveCategory: "Select leave type",
    leaveCategoryRequired: "Please select a leave type",
    startDateRequired: "Please select a start date",
    endDateRequired: "Please select an end date",
    endAfterStart: "End date must be after start date",
    selectStartDate: "Select start date",
    selectEndDate: "Select end date",
    leaveDaysLabel: "Leave days:",
    submitting: "Submitting...",
    submitRequest: "Submit Request",
    submitSuccess: "Leave request submitted successfully",
    submitFailed: "Failed to submit leave request",
    cancelConfirm: "Are you sure you want to cancel this request?",
    cancelSuccess: "Leave request cancelled successfully",
    cancelFailed: "Failed to cancel request",
    fetchFailed: "Failed to fetch leave requests",
    employeeIdMissing: "Employee ID is not available",
    notAvailable: "Not available",
    from: "From:",
    to: "To:",
    daysCount: "{count} day(s)",
    requestDate: "Request Date:",
    cancel: "Cancel",
    admin: {
      title: "Official Holidays",
      description: "Manage official holidays for organization and branches",
      addHoliday: "Add Holiday",
      addNewHoliday: "Add New Holiday",
      holidayName: "Holiday Name",
      holidayNamePlaceholder: "Enter holiday name",
      date: "Date",
      branch: "Branch",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Enter holiday description",
      allBranchesOrg: "All Branches (Organization)",
      allBranches: "All Branches",
      add: "Add",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      durationDays: "Duration (Days)",
      actions: "Actions",
      status: "Status",
      active: "Active",
      inactive: "Inactive",
      isActive: "Holiday is active",
      employees: "Employees",
      employeesHint: "Leave empty to apply to everyone in the branch/org. Or pick specific employees (no branch needed then).",
      branchHint: "Optional. Only used when no specific employees are selected.",
      branchHiddenHint: "Scope follows the selected employees — no branch selection needed.",
      scopeByEmployees: "By employees",
      searchEmployees: "Search employees...",
      loadingEmployees: "Loading employees...",
      noEmployees: "No employees found",
      selectAll: "Select all",
      clearSelection: "Clear selection",
      noEmployeesSelected: "None selected — applies to everyone in the chosen scope",
      selectedEmployeesCount: "{count} employees selected",
      employeesCount: "{count} employees",
      allBranchEmployees: "All branch employees",
      allOrgEmployees: "All organization employees",
      filterYear: "Year",
      filterBranch: "Branch",
      filterStatus: "Status",
      filterAllBranches: "All branches",
      filterOrgWide: "Organization-wide only",
      filterAllStatuses: "All statuses",
      seedEgyptian: "Seed Egyptian holidays",
      seeding: "Seeding...",
      seedConfirm: "Add Egyptian public holidays for this year?",
      seedSuccess: "Egyptian holidays seeded successfully",
      seedFailed: "Failed to seed Egyptian holidays",
      fetchEmployeesFailed: "Failed to load employees",
      noHolidays: "No holidays found",
      fillRequired: "Please fill all required fields",
      createSuccess: "Holiday created successfully",
      createFailed: "Failed to create holiday",
      editHoliday: "Edit Holiday",
      fetchFailed: "Failed to load holidays",
      updateSuccess: "Holiday updated successfully",
      updateFailed: "Failed to update holiday",
      deleteConfirm: "Are you sure you want to delete this holiday?",
      deleteSuccess: "Holiday deleted successfully",
      deleteFailed: "Failed to delete holiday"
    }
  },
  permissions: {
    title: "Permissions",
    subtitle: "Review and approve employee permission requests",
    submittedRequests: "Submitted Requests",
    filterByStatus: "Filter by status:",
    all: "All",
    loading: "Loading...",
    noRequests: "No permission requests found",
    fetchFailed: "Failed to load permission requests",
    refresh: "Refresh",
    approve: "Approve",
    reject: "Reject",
    forwardToAdmin: "Forward to Admin",
    takeOver: "Take Over",
    approveTitle: "Approve Request",
    rejectTitle: "Reject Request",
    approveNotes: "Notes (optional)",
    approveNotesPlaceholder: "Enter approval notes",
    rejectReason: "Rejection reason",
    rejectReasonPlaceholder: "Enter rejection reason",
    rejectReasonRequired: "Please enter a rejection reason",
    cancel: "Cancel",
    confirmApprove: "Confirm Approval",
    confirmReject: "Confirm Rejection",
    approveSuccess: "Request approved successfully",
    approveFailed: "Failed to approve request",
    rejectSuccess: "Request rejected successfully",
    rejectFailed: "Failed to reject request",
    forwardSuccess: "Request forwarded to admin",
    forwardFailed: "Failed to forward request",
    takeOverSuccess: "Request taken over successfully",
    takeOverFailed: "Failed to take over request",
    status: {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      cancelled: "Cancelled",
      forwarded: "Forwarded"
    },
    table: {
      employee: "Employee",
      type: "Permission Type",
      date: "Date",
      time: "Time",
      reason: "Reason",
      status: "Status",
      actions: "Actions"
    },
    previous: "Previous",
    next: "Next",
    page: "Page",
    of: "of",
    requests: "requests",
  },
  company: {
    title: "Company Management",
    description: "Description",
    branches: "Company Branches",
    allBranches: "All company branches",
    addBranch: "Add New Branch",
    branchName: "Branch Name",
    enterBranchName: "Enter branch name",
    noBranches: "No branches",
    cannotDeleteLast: "Cannot delete last branch",
    alexandriaBranch: "Alexandria Branch",
    cairoBranch: "Cairo Branch",
    active: "Active",
    inactive: "Inactive",
    code: "Code",
    manager: "Manager",
    departments: "Departments",
    employees: "Employees",
    email: "Email",
    phone: "Phone Number",
    address: "Address",
    emailPlaceholder: "example@company.com",
    phonePlaceholder: "01012345678",
    managerPlaceholder: "Enter manager name",
    addressPlaceholder: "Enter branch address",
    descriptionPlaceholder: "Enter branch description (optional)",
    departmentsPlaceholder: "0",
    employeesPlaceholder: "0",
    tabs: {
      branches: "Branches",
      departments: "Departments",
      documents: "Documents",
      assets: "Assets",
      tools: "Tools",
      jobPositions: "Job Positions",
      leaveRequests: "Leave Requests",
      devices: "Devices",
      admins: "Admins",
      orders: "Couriers",
      documentApprovals: "Document Approvals",
      transfers: "Transfers"
    },
    admins: {
      description: "Create and manage organization administrators",
      createNew: "Create New Admin",
      email: "Email",
      emailPlaceholder: "admin@example.com",
      username: "Username",
      usernamePlaceholder: "Enter username",
      password: "Password",
      passwordPlaceholder: "Enter password",
      firstName: "First Name",
      firstNamePlaceholder: "Enter first name",
      lastName: "Last Name",
      lastNamePlaceholder: "Enter last name",
      phone: "Phone",
      phonePlaceholder: "+201000110001",
      nationalID: "National ID",
      nationalIDPlaceholder: "Enter national ID",
      create: "Create Admin",
      creating: "Creating..."
    },
    content: {
      allCompanyDepartments: "All company departments",
      uploadDocumentForEmployees: "Upload document for employees",
      uploadDocumentDescription: "Upload a document and send it to selected employees",
      pendingDevices: "Pending Devices",
      pendingRequests: "pending request",
      employeeDevices: "Employee Devices",
      file: "File",
      changeFile: "Change File",
      clickToSelectFile: "Click to select file",
      orDragFileHere: "or drag file here",
      documentTitle: "Document Title",
      enterDocumentTitle: "Enter document title",
      documentDescription: "Document Description",
      optional: "(optional)",
      enterDocumentDescription: "Enter document description (optional)",
      selectEmployees: "Select Employees",
      optionalLeaveEmptyForAll: "(optional - leave empty to send to all)",
      selectAll: "Select All",
      deselectAll: "Deselect All",
      loadingEmployees: "Loading employees...",
      noEmployeesAvailable: "No employees available",
      noEmployeesOrLoading: "No employees or loading...",
      selectEmployeeOptional: "Select employee (optional)",
      employeesSelected: "Selected",
      employee: "employee",
      uploading: "Uploading...",
      uploadDocument: "Upload Document",
      refresh: "Refresh",
      uploadedDocuments: "Uploaded Documents",
      totalDocuments: "Total",
      document: "document",
      all: "All",
      company: "Company",
      employees: "Employees",
      employeeBadge: "Employee",
      companyBadge: "Company",
      untitledDocument: "Untitled document",
      clickEmployeeToViewDocuments: "Click an employee name to view all their documents",
      noCompanyDocuments: "No company documents",
      noEmployeeDocuments: "No employee documents",
      noDocumentsUploaded: "No documents uploaded",
      startUploadingAbove: "Start by uploading a new document above",
      loading: "Loading...",
      assets: "Assets",
      manageCompanyEquipment: "Manage company assets",
      noEquipment: "No assets",
      addEquipment: "Add Asset",
      editBranch: "Edit Branch",
      status: "Status",
      selectStatus: "Select status",
      active: "Active",
      inactive: "Inactive",
      branchLocation: "Branch Location (optional)",
      latitude: "Latitude",
      longitude: "Longitude",
      areaRadius: "Area Radius in meters (Radius)",
      useMyCurrentLocation: "Use my current location",
      updateLocationOnMap: "Update location on map",
      branchSettings: "Branch Settings",
      loadingBranchSettings: "Loading branch settings...",
      startTime: "Start Time",
      endTime: "End Time",
      gracePeriod: "Grace Period (in minutes)",
      selectAtLeastOneWorkDay: "Please select at least one work day",
      scheduleInheritanceTitle: "Branch work schedule",
      scheduleInheritanceHint: "If you choose \"Follow company\", employees in this branch use the main company settings (work days, hours, grace period, and timezone). If you customize, settings apply only to this branch's employees.",
      useOrgWorkSchedule: "Follow company",
      useOrgWorkScheduleHint: "Work days, hours, grace period, and timezone will be taken from the main company settings.",
      branchShiftsTitle: "Branch shifts",
      useOrgShifts: "Inherit company shifts",
      useOrgShiftsHint: "Use shifts defined in company settings. Employees of this branch are assigned company shifts.",
      customizeBranchShifts: "Customize branch shifts",
      customizeBranchShiftsHint: "Define shifts specific to this branch, independent of company shifts.",
      customizeBranchSchedule: "Customize for this branch",
      customizeBranchScheduleHint: "Set independent settings for this branch only (work days, hours, grace period, and timezone).",
      fromOrganization: "Follow company",
      followCompanyLatePenalty: "Follow company",
      followCompanyLatePenaltyHint: "Late penalty settings will be taken from the main company.",
      customizeBranchLatePenalty: "Customize for this branch",
      customizeBranchLatePenaltyHint: "Set late penalty rules for this branch only.",
      autoCheckoutSettings: "Auto checkout settings",
      followCompanyAutoCheckout: "Follow company",
      followCompanyAutoCheckoutHint: "Auto checkout settings will be taken from the main company.",
      customizeBranchAutoCheckout: "Customize for this branch",
      customizeBranchAutoCheckoutHint: "Set auto checkout settings for this branch only.",
      autoCheckoutEnabled: "Enable auto checkout",
      autoCheckoutGraceHours: "Hours after shift end",
      autoCheckoutGraceHoursHint: "Hours after work end before auto checkout (0–24)",
      autoCheckoutGraceHoursInvalid: "Hours must be between 0 and 24",
      autoCheckoutCountOvertime: "Count overtime",
      autoCheckoutNotifyEmployee: "Notify employee",
      autoCheckoutNotifyAdmin: "Notify admin",
      autoCheckoutDeductionSettings: "Missed checkout deduction",
      followCompanyAutoCheckoutDeduction: "Follow company",
      followCompanyAutoCheckoutDeductionHint: "Deduction for a missed checkout will be taken from the main company.",
      customizeBranchAutoCheckoutDeduction: "Customize for this branch",
      customizeBranchAutoCheckoutDeductionHint: "Set a deduction policy for this branch only, including no deduction.",
      autoCheckoutDeductionPolicy: "Deduction policy",
      autoCheckoutDeductionFree: "No deduction",
      autoCheckoutDeductionQuarterDay: "Quarter day",
      autoCheckoutDeductionHalfDay: "Half day",
      autoCheckoutDeductionFixedAmount: "Fixed amount",
      autoCheckoutDeductionFixedAmountHint: "Amount deducted in the payroll currency",
      autoCheckoutDeductionFixedAmountInvalid: "Enter a positive amount with at most 2 decimals",
      hoursUnit: "hours",
      checkInSelfieSettings: "Check-in selfie settings",
      followCompanyCheckInSelfie: "Follow company",
      followCompanyCheckInSelfieHint: "Selfie policy will be taken from the main company.",
      customizeBranchCheckInSelfie: "Customize for this branch",
      customizeBranchCheckInSelfieHint: "Set selfie mode and work types for this branch only.",
      checkInSelfieMode: "Selfie mode",
      checkInSelfieApplyTo: "Apply to work types",
      checkInSelfieApplyToRequired: "Select at least one work type",
      checkInSelfieModes: {
        off: "Off",
        optional: "Optional",
        required: "Required",
      },
      checkInSelfieWorkTypes: {
        office: "Office",
        remote: "Remote",
        hybrid: "Hybrid",
        delivery: "Delivery",
      },
      useOrgScheduledRemote: "Follow company scheduled remote days",
      useOrgScheduledRemoteHint: "Use scheduled remote-work days from the main company settings.",
      customizeBranchScheduledRemote: "Customize remote days for this branch",
      customizeBranchScheduledRemoteHint: "Set scheduled remote days and employee scope for this branch only.",
      scheduledRemoteFollowCompanyLocked: "Locked while following company settings. Choose “Customize remote days for this branch” to edit these options.",
      scheduledRemoteTitle: "Scheduled remote work days",
      scheduledRemoteHint: "This does not change the employee’s permanent work location. On selected days they are treated as Remote, then automatically revert to Office or Hybrid.",
      scheduledRemoteEnable: "Enable scheduled remote days",
      scheduledRemoteDays: "Weekdays",
      scheduledRemoteScope: "Apply to",
      scheduledRemoteEmployees: "Employees",
      scheduledRemoteDepartments: "Departments",
      scheduledRemoteSearchEmployees: "Search by name or email...",
      scheduledRemoteNoEmployees: "No employees",
      scheduledRemoteNoDepartments: "No departments",
      scheduledRemoteScopeAll: "All employees",
      scheduledRemoteScopeSelected: "Selected employees",
      scheduledRemoteScopeDepartment: "Selected departments",
      latePenaltySettings: "Late Penalty Settings",
      enable: "Enable",
      penaltyRules: "Penalty Rules",
      addRule: "Add Rule",
      noRulesMessage: "No rules. Click \"Add Rule\" to add a new rule.",
      rule: "Rule",
      delayMinutes: "Delay Minutes *",
      penaltyType: "Penalty Type",
      selectPenaltyType: "Select penalty type",
      none: "None",
      quarterDay: "Quarter Day",
      halfDay: "Half Day",
      fullDay: "Full Day",
      custom: "Custom",
      penaltyAmount: "Penalty Amount",
      penaltyDays: "Penalty Days",
      tools: {
        title: "Tools",
        totalTools: "Total",
        tool: "tool",
        addTool: "Add Tool",
        noTools: "No tools",
        startAddingTool: "Start by adding a new tool above",
        assignmentCount: "Assignment count:",
        creationDate: "Creation date:",
        showAssignedEmployees: "Show assigned employees",
        hideAssignedEmployees: "Hide assigned employees",
        assignedAt: "Assigned at:",
        addNewTool: "Add New Tool",
        toolName: "Tool Name *",
        toolNamePlaceholder: "Example: ChatGPT Pro",
        adding: "Adding...",
        add: "Add",
        editTool: "Edit Tool",
        editToolPlaceholder: "Example: ChatGPT Pro - Updated",
        updating: "Updating...",
        update: "Update",
        assignTool: "Assign Tool:",
        selectAll: "Select All",
        deselectAll: "Deselect All",
        loadingEmployees: "Loading employees...",
        noAvailableEmployees: "No employees available",
        employeesSelected: "Selected",
        employee: "employee",
        assigning: "Assigning...",
        assign: "Assign",
        removeTool: "Remove Tool:",
        employeesSelectedForRemoval: "Selected",
        employeesForRemoval: "employee(s) for removal",
        removing: "Removing...",
        remove: "Remove",
        fetchFailed: "Failed to fetch tools"
      },
      jobPositions: {
        title: "Job Positions",
        allPositions: "All job positions",
        addNewPosition: "Add New Position",
        noPositions: "No job positions",
        positionName: "Position Name *",
        enterPositionName: "Enter position name",
        description: "Description",
        enterDescription: "Enter position description (optional)",
        status: "Status",
        selectStatus: "Select status",
        order: "Order:",
        creating: "Creating...",
        create: "Create",
        createSuccess: "Position created successfully",
        createFailed: "Failed to create position",
        deleteConfirm: "Are you sure you want to delete this position?",
        deleteSuccess: "Position deleted successfully",
        deleteFailed: "Failed to delete position",
        fetchFailed: "Failed to load job positions",
        orgIdNotFound: "Organization ID not found. Please log out and log in again.",
        invalidOrgIdFormat: "Invalid organization ID format. Must be in XX-0000 format (e.g., DE-5292)",
        enterPositionNameRequired: "Please enter position name"
      },
      leaveRequests: {
        title: "Leave Requests",
        request: "request",
        noRequests: "No leave requests",
        employee: "Employee:",
        department: "Department:",
        position: "Position:",
        from: "From:",
        to: "To:",
        daysCount: "Days:",
        day: "day",
        reason: "Reason:",
        leaveCategory: "Leave Category:",
        leaveType: "Leave Type:",
        paid: "Paid",
        unpaid: "Unpaid",
        rejectionReason: "Rejection Reason:",
        notes: "Notes:",
        requestDate: "Request Date:",
        approve: "Approve",
        reject: "Reject",
        previous: "Previous",
        next: "Next",
        page: "Page",
        of: "of",
        approveTitle: "Approve Leave Request",
        leaveTypeLabel: "Leave Type",
        notesLabel: "Notes (optional)",
        notesPlaceholder: "Enter additional notes",
        modifyDays: "Modify Number of Days (optional)",
        modifyDaysPlaceholder: "Leave empty to keep original number of days",
        processing: "Processing...",
        approveSuccess: "Leave request approved successfully",
        approveFailed: "Failed to approve request",
        rejectTitle: "Reject Leave Request",
        rejectReasonLabel: "Rejection Reason *",
        rejectReasonPlaceholder: "Enter reason for rejecting leave request",
        rejectReasonRequired: "Please enter rejection reason",
        rejectSuccess: "Leave request rejected successfully",
        rejectFailed: "Failed to reject request",
        fetchFailed: "Failed to load leave requests",
        unknown: "Unknown",
        notSpecified: "Not specified",
        cancelled: "Cancelled"
      },
      devices: {
        title: "Employee Devices",
        device: "device",
        noDevices: "No devices",
        noPendingRequests: "No pending device requests",
        status: {
          approved: "Approved",
          pending: "Pending",
          rejected: "Rejected"
        },
        deviceType: "Device Type:",
        brand: "Brand:",
        deviceName: "Device Name:",
        model: "Model:",
        deviceId: "Device ID:",
        system: "Operating System:",
        type: "Type:",
        carrier: "Carrier:",
        requestDate: "Request Date:",
        reviewDate: "Review Date:",
        id: "ID:",
        unknown: "Unknown",
        notSpecified: "Not specified",
        unknownDevice: "Unknown device",
        approve: "Approve",
        reject: "Reject",
        approveSuccess: "Device request approved successfully",
        approveFailed: "Failed to approve device request",
        rejectSuccess: "Device request rejected successfully",
        rejectFailed: "Failed to reject device request",
        rejectReasonPrompt: "Please enter rejection reason:",
        deleteConfirm: "Are you sure you want to delete this device?",
        deleteSuccess: "Device deleted successfully",
        deleteFailed: "Failed to delete device",
        deviceIdNotFound: "Device ID not found",
        previous: "Previous",
        next: "Next",
        page: "Page",
        of: "of"
      },
      buttonTitles: {
        refreshList: "Refresh List",
        branchSettings: "Branch Settings",
        viewBranchLocation: "View Branch Location",
        editBranch: "Edit Branch",
        deleteBranch: "Delete Branch",
        delete: "Delete",
        reassign: "Reassign",
        unassign: "Unassign",
        assign: "Assign",
        readingStatistics: "Reading Statistics",
        viewDocument: "View Document",
        deleteDocument: "Delete Document",
        edit: "Edit",
        assignToEmployees: "Assign to Employees",
        removeFromEmployees: "Remove from Employees",
        deleteDevice: "Delete Device",
        close: "Close"
      },
      confirmDeleteBranch: "Are you sure you want to delete this branch?",
      confirmDeleteDepartment: "Are you sure you want to delete this department?",
      confirmDeleteEquipment: "Are you sure you want to delete this asset?",
      confirmUnassignEquipment: "Are you sure you want to unassign this asset?",
      confirmDeleteTool: "Are you sure you want to delete this tool?",
      confirmDeleteDocument: "Are you sure you want to delete the document \"{title}\"?",
      customPenaltyNeedsAmountOrDays: "Custom penalty requires an amount or number of days",
      createWithAssign: "Create and assign",
      createWithoutAssign: "Create without assigning",
      creating: "Creating...",
      orgNotFound: "Organization not found",
      addNewEquipment: "Add new asset",
      equipmentNameRequired: "Asset name *",
      equipmentNamePlaceholder: "e.g. Dell Latitude 5540 laptop",
      equipmentDescPlaceholder: "e.g. Laptop for office work",
      serialNumber: "Serial number",
      serialNumberPlaceholder: "e.g. DL-2024-001",
      priceEGPRequired: "Price (EGP) *",
      pricePlaceholder: "e.g. 25000",
      assignToEmployeeOptional: "Assign to employee (optional)",
      withoutAssignment: "No assignment",
      ordersCount: "{n} orders",
      loadMore: "Load more",
      showingOf: "Showing {shown} of {total}",
      noOrders: "No orders",
      viewEmployeeDetails: "View employee details",
      employeeLabel: "Employee:",
      emailLabel: "Email:",
      phoneLabel: "Phone:",
      departmentLabel: "Department:",
      positionLabel: "Position:",
      orderDateLabel: "Order date:",
      locationLabel: "Location:",
      createdAtLabel: "Created at:",
      viewOnMap: "View on map",
      perPageOption: "{n} per page",
      enterCount: "Enter count",
      apply: "Apply",
      pageOfOrders: "Page {page} of {pages} ({total} orders)",
      editEquipment: "Edit Asset",
      assignEquipment: "Assign Asset",
      selectEmployeeRequired: "Select employee *",
      selectEmployeePlaceholder: "Select employee",
      selectNewEmployeeRequired: "Select new employee *",
      selectNewEmployeePlaceholder: "Select new employee",
      equipmentColon: "Asset:",
      currentlyAssigned: "Currently assigned:",
      assigning: "Assigning...",
      reassigning: "Reassigning...",
      assigned: "Assigned",
      returned: "Returned",
      serialNumberColon: "Serial number:",
      priceLabel: "Price",
      egp: "EGP",
      assignedToColon: "Assigned to:",
      assignmentDateColon: "Assignment date:",
      autoPenaltyCalculated: "This penalty is calculated automatically based on working hours",
      setWorkStartEndRequired: "Please set branch work start and end times",
      selectTimezoneRequired: "Please select a timezone for the branch",
      addLatePenaltyRuleOrDisable: "Add at least one late penalty rule or disable late penalties",
      invalidLatePenaltyType: "Each late penalty rule must have a valid penalty type",
      totalReads: "Total reads",
      employeesWhoRead: "Employees who read",
      readersList: "Readers list",
      unknownEmployee: "Unknown employee",
      addressLabel: "Address:",
      coordinatesLabel: "Coordinates:",
      areaRadiusLabel: "Area radius:",
      meters: "meters",
      addNewDepartment: "Add new department",
      editDepartment: "Edit department",
      deleteDepartment: "Delete department",
      editDepartmentHint: "The name will be updated automatically for all employees in this department.",
      editPosition: "Edit Position",
      editPositionHint: "The name will be updated automatically for all employees with this position.",
      departmentName: "Department name",
      enterDepartmentName: "Enter department name",
      saving: "Saving...",
      toasts: {
        loadBranchesFailed: "Error {status}: Failed to load branches",
        fetchDevicesFailed: "Failed to fetch devices data",
        fetchPendingDeviceRequestsFailed: "Failed to fetch pending device requests",
        createBranchSuccess: "Branch created successfully",
        createBranchFailed: "Failed to create branch",
        deleteBranchSuccess: "Branch deleted successfully",
        deleteBranchFailed: "Failed to delete branch",
        updateBranchSuccess: "Branch updated successfully",
        updateBranchFailed: "Failed to update branch",
        loadBranchSettingsFailed: "Failed to load branch settings. Local saved data will be used.",
        updateBranchSettingsSuccess: "Branch settings updated successfully",
        updateBranchSettingsFailed: "Failed to update branch settings",
        loadDepartmentsFailed: "Failed to load departments",
        fetchOrdersFailed: "Failed to fetch orders data",
        createDepartmentSuccess: "Department created successfully",
        createDepartmentFailed: "Failed to create department",
        updateDepartmentSuccess: "Department name updated successfully",
        updateDepartmentFailed: "Failed to update department name",
        updateDepartmentEmployeesUpdated: "({count} employees updated)",
        updatePositionSuccess: "Position name updated successfully",
        updatePositionFailed: "Failed to update position name",
        updatePositionEmployeesUpdated: "({count} employees updated)",
        deleteDepartmentSuccess: "Department deleted successfully",
        deleteDepartmentFailed: "Failed to delete department",
        fetchEquipmentFailed: "Failed to fetch assets data",
        createEquipmentSuccess: "Asset created successfully",
        createEquipmentAssignedSuccess: "Asset created and assigned successfully",
        createEquipmentFailed: "Failed to create asset",
        updateEquipmentSuccess: "Asset updated successfully",
        updateEquipmentFailed: "Failed to update asset",
        deleteEquipmentSuccess: "Asset deleted successfully",
        deleteEquipmentFailed: "Failed to delete asset",
        assignEquipmentSuccess: "Asset assigned successfully",
        assignEquipmentFailed: "Failed to assign asset",
        unassignEquipmentSuccess: "Asset unassigned successfully",
        unassignEquipmentFailed: "Failed to unassign asset",
        reassignEquipmentSuccess: "Asset reassigned successfully",
        reassignEquipmentFailed: "Failed to reassign asset",
        fetchDocumentsFailed: "Failed to fetch documents",
        fetchDocumentStatsFailed: "Failed to fetch document statistics",
        deleteDocumentSuccess: "Document deleted successfully",
        deleteDocumentAffectedEmployees: " (updated {count} employees)",
        deleteDocumentFailed: "Failed to delete document",
        uploadDocumentSuccess: "Document uploaded successfully",
        uploadDocumentSummary: " (updated {updated} employees, notified {notified} employees)",
        uploadDocumentFailed: "Failed to upload document",
        fetchEmployeesFailed: "Failed to fetch employees",
        createToolSuccess: "Tool created successfully",
        createToolFailed: "Failed to create tool",
        updateToolSuccess: "Tool updated successfully",
        updateToolFailed: "Failed to update tool",
        deleteToolSuccess: "Tool deleted successfully",
        deleteToolFailed: "Failed to delete tool",
        assignToolSuccess: "Tool assigned to {count} employees successfully",
        assignToolFailed: "Failed to assign tool",
        removeToolSuccess: "Tool removed from {count} employees successfully",
        removeToolFailed: "Failed to remove tool",
        createAdminSuccess: "Admin created successfully",
        createAdminFailed: "Failed to create admin",
        onlyOrgAdminsCanCreate: "Only organization admins can create additional admins"
      }
    },
    days: {
      sunday: "Sunday",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday"
    }
  },
  reports: {
    title: "Reports",
    description: "View and manage all system reports",
    generalInfo: "General Information",
    generalInfoDesc: "Display general information about employees and company",
    salaryInfo: "Salary Information",
    salaryInfoDesc: "Comprehensive reports on salaries and allowances",
    employmentInfo: "Employment Information",
    employmentInfoDesc: "Employment details and appointment date",
    leaveBalance: "Leave Balance",
    leaveBalanceDesc: "Display remaining leave balance for each employee",
    annualLeaves: "Annual Leaves",
    annualLeavesDesc: "Record all leaves during the year",
    contactInfo: "Contact Information",
    contactInfoDesc: "Contact data for all employees",
    birthdays: "Birthdays",
    birthdaysDesc: "List of employee birthdays",
    emergencyContact: "Emergency Contact",
    emergencyContactDesc: "Emergency contact information",
    probationPeriods: "Probation Periods",
    probationPeriodsDesc: "Track probation periods for new employees",
    formerEmployees: "Former Employees",
    formerEmployeesDesc: "Record of employees who left the company",
    bankAccounts: "Bank Accounts",
    bankAccountsDesc: "Bank account information for employees",
    fixedAssets: "Fixed Assets",
    fixedAssetsDesc: "Fixed assets and inventory record",
    attendanceData: "Attendance Data",
    attendanceDataDesc: "Detailed reports on attendance and departure",
    disciplinaryReport: "Disciplinary Report",
    disciplinaryReportDesc: "Record of disciplinary actions and warnings",
    permissionRequests: "Permission Requests",
    permissionRequestsDesc: "Track permission requests and approvals",
    backToReports: "Back to Reports",
    reportNotFound: "Report Not Found",
    reportNotFoundDesc: "The requested report does not exist or has been deleted.",
    loadingData: "Loading data...",
    errorLoadingData: "An error occurred while loading data",
    errorFetchingData: "An error occurred while fetching data. Please try again.",
    errorExportingFile: "An error occurred while exporting the file",
    dataLabel: "Data",
    reportSheetName: "Report",
    totalEmployees: "Total Employees",
    activeEmployees: "Active Employees",
    onLeaveEmployees: "On Leave",
    totalDepartments: "Total Departments",
    averageSalary: "Average Salary",
    totalSalary: "Total Salary",
    maxSalary: "Maximum Salary",
    minSalary: "Minimum Salary",
    totalAllowances: "Total Allowances",
    totalDeductions: "Total Deductions",
    totalFormerEmployees: "Total Former Employees",
    employeeList: "Employee List",
    downloadExcel: "Download Excel",
    no: "No",
    serialNo: "#",
    name: "Name",
    email: "Email",
    phone: "Phone",
    department: "Department",
    position: "Position",
    status: "Status",
    joinDate: "Join Date",
    workLocation: "Work Location",
    baseSalary: "Base Salary",
    allowances: "Allowances",
    deductions: "Deductions",
    netSalary: "Net Salary",
    approvalStatus: "Approval Status",
    startDate: "Start Date",
    directManager: "Direct Manager",
    teamLeader: "Team Leader",
    distributionByStatus: "Distribution by Status",
    birthDate: "Birth Date",
    birthday: "Birthday",
    daysUntilBirthday: "Days Until Birthday",
    probationEndDate: "Probation End Date",
    daysRemaining: "Days Remaining",
    daysInProbation: "Days in Probation",
    endDate: "End Date",
    reason: "Reason",
    requestDate: "Request Date",
    duration: "Duration (Days)",
    approvedBy: "Approved By",
    rejectionReason: "Rejection Reason",
    date: "Date",
    checkIn: "Check In",
    checkOut: "Check Out",
    workHours: "Work Hours",
    overtimeHours: "Overtime Hours",
    earlyLeave: "Early Leave",
    deductionAmount: "Deduction Amount",
    assetName: "Asset Name",
    assetStatus: "Status",
    assignedTo: "Assigned To",
    assetPrice: "Price",
    assets: "Assets",
    assetsCount: "Number of Assets",
    assignedDate: "Assigned Date",
    serialNumber: "Serial Number",
    noAssets: "No assets",
    noFixedAssetsData: "No fixed assets data available",
    totalAssets: "Total Assets",
    employeesWithAssets: "Employees with Assets",
    issuedAssets: "Issued Assets",
    bankName: "Bank Name",
    accountNumber: "Account Number",
    iban: "IBAN",
    noEmployeeData: "No employee data available",
    noDataAvailable: "No data available",
    employeesWithSalary: "Employees with Salary",
    notSpecified: "Not Specified",
    active: "Active",
    inactive: "Inactive",
    onLeave: "On Leave",
    underTraining: "Under Training",
    serviceEnded: "Service Ended",
    resigned: "Resigned",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    underReview: "Under Review",
    approvedPlural: "Approved",
    teamLeaders: "Team Leaders",
    distributionByDepartments: "Distribution by Departments",
    departmentLabels: {
      management: "Management"
    },
    totalApprovedLeaves: "Total Approved Leaves",
    totalPendingLeaves: "Total Pending Leaves",
    averageLeaveBalance: "Average Leave Balance",
    totalLeaveBalance: "Total Leave Balance",
    totalRejectedLeaves: "Total Rejected Leaves",
    totalLeaveRequests: "Total Leave Requests",
    totalLeaves: "Total Leaves",
    totalPendingLeavesDays: "Total Pending Leave Days",
    pendingCount: "Pending (Count)",
    pendingDays: "Pending (Days)",
    approvedCount: "Approved (Count)",
    approvedDays: "Approved (Days)",
    totalDeductionsLabel: "Total Deductions",
    selectYear: "Select Year",
    year: "Year",
    totalAllLeaves: "Total All Leaves",
    totalApprovedLeaveDays: "Total Approved Leave Days",
    totalAllLeaveDays: "Total All Leave Days",
    annualLeavesTitle: "Annual Leaves",
    totalPermissionDays: "Total Permission Days",
    employeesWithEmail: "Employees with Email",
    employeesWithPhone: "Employees with Phone",
    employeesWithEmergencyContact: "Employees with Emergency Contact",
    employeesWithBankAccount: "Employees with Bank Account",
    employeesWithIBAN: "Employees with IBAN",
    employeesWithSwiftCode: "Employees with Swift Code",
    employeesWithDisciplinary: "Employees with Disciplinary Actions",
    address: "Address",
    probationCompleted: "Probation Completed",
    inProbationPeriod: "In Probation Period",
    averageDaysRemaining: "Average Days Remaining",
    completed: "Completed",
    leaveTypeAnnual: "Annual",
    leaveTypeSick: "Sick",
    leaveType: "Leave Type",
    selectDays: "Select Days",
    notAvailable: "Not Available",
    selectTimePeriod: "Select Time Period",
    fromDate: "From Date:",
    toDate: "To Date:",
    search: "Search",
    totalRecords: "Total Records",
    numberOfEmployees: "Number of Employees",
    present: "Present on time",
    late: "Late",
    absent: "Absent",
    totalAbsences: "Total Absences",
    totalLateArrivals: "Total Late Arrivals",
    absences: "Absences",
    lateArrivals: "Late Arrivals",
    hour: "hour",
    hours: "hours",
    yes: "Yes",
    noAttendanceDataForPeriod: "No attendance data available for the selected period",
    attendanceDataReport: "Attendance Data",
    totalPositions: "Total Positions",
    totalStatuses: "Total Statuses",
    all: "All",
    totalDisciplinaryActions: "Total Disciplinary Actions",
    totalWarnings: "Total Warnings",
    totalPenalties: "Total Penalties",
    totalPenaltyAmount: "Total Penalty Amount",
    disciplinaryActions: "Disciplinary Actions",
    warnings: "Warnings",
    penalties: "Penalties",
    selectRequestStatus: "Select Request Status",
    requestStatus: "Status:",
    totalRequests: "Total Requests",
    noPermissionRequestsForStatus: "No permission requests available for the selected status",
    departureDate: "Departure Date",
    paymentMethod: "Payment Method",
    bankTransfer: "Bank Transfer",
    noContactData: "No contact data available",
    totalWorkingHours: "Total Working Hours",
    totalOvertimeHours: "Total Overtime Hours",
    noSalaryData: "No salary data available",
    salaryBreakdown: {
      title: "Salary breakdown",
      clickHint: "Click an employee to view their salary explanation",
      close: "Close",
      ok: "OK",
      prorationTitle: "Why is the amount lower than the assigned salary?",
      prorationExplain: "Because the join date is {date}, base salary, allowances, and bonuses are calculated for working days this month ({actual} days out of {total} working days).",
      baseSalaryExplain: "This is the full base salary assigned by management.",
      baseSalaryExplainProrated: "This is the full monthly base salary. The amount due is prorated for this month based on the join date.",
      thisMonthBase: "This month's base (after proration)",
      bonuses: "Bonuses",
      totalBonuses: "Total bonuses",
      overtime: "Overtime",
      noAllowances: "No allowances assigned currently",
      noBonuses: "No bonuses this month",
      noDeductions: "No deductions this month",
      amountDue: "Amount due (net salary)",
      amountDueHint: "After allowances, bonuses, and deductions",
      amountDueHintProrated: "Due for {actual} days out of {total} working days",
      netFormula: "Net = calculated base + allowances + bonuses − deductions",
    },
    noEmploymentData: "No employment data available",
    noLeaveBalanceData: "No leave balance data available",
    noAnnualLeavesData: "No annual leaves data available for year",
    leaveDetails: "Leave Details:",
    from: "From",
    to: "To",
    totalBirthdays: "Total Birthdays",
    upcomingBirthdays: "Upcoming Birthdays",
    daysUntil: "Days Until",
    age: "Age",
    noBirthdaysInPeriod: "No upcoming birthdays in the next",
    noProbationData: "No probation periods data available",
    noDisciplinaryData: "No disciplinary data available",
    disciplinaryDetails: "Disciplinary Details:",
    penaltyAmount: "Penalty Amount:",
    day: "day",
    days: "days",
    rejectedCount: "Rejected (Count)",
    rejectedDays: "Rejected (Days)",
    annual: "Annual",
    sick: "Sick",
    export: {
      notSpecified: "Not Specified",
      notAvailable: "Not Available",
      leaveAnnual: "Annual",
      leaveSick: "Sick",
      leaveApproved: "Approved",
      leavePending: "Pending",
      leaveRejected: "Rejected",
      male: "Male",
      female: "Female",
      yes: "Yes",
      no: "No",
      approvedSingular: "Approved",
      penalty: "Penalty",
      warning: "Warning",
      bankTransfer: "Bank Transfer",
      leaveDetails: "Leave Details",
      disciplinaryDetails: "Disciplinary Details",
      employeesWithAddress: "Employees with Address",
      numberOfDays: "Number of Days",
      numberOfPositions: "Number of Positions",
      numberOfStatuses: "Number of Statuses",
      daysSuffix: "days",
      daySuffix: "day",
      headers: {
        number: "#",
        employeeNumber: "Employee Number",
        name: "Name",
        employeeName: "Employee Name",
        email: "Email",
        phone: "Phone",
        department: "Department",
        position: "Position",
        status: "Status",
        joinDate: "Join Date",
        nationalId: "National ID",
        gender: "Gender",
        birthDate: "Birth Date",
        birthday: "Birthday",
        address: "Address",
        workLocation: "Work Location",
        directManager: "Direct Manager",
        baseSalary: "Base Salary",
        allowances: "Allowances",
        deductions: "Deductions",
        netSalary: "Net Salary",
        approvalStatus: "Approval Status",
        startDate: "Start Date",
        endDate: "End Date",
        teamLead: "Team Lead",
        periodOfStay: "Period of Stay (Days)",
        totalLeaves: "Total Leaves",
        approved: "Approved",
        pending: "Pending",
        rejected: "Rejected",
        leaveBalance: "Leave Balance",
        year: "Year",
        approvedCount: "Approved (Count)",
        approvedDays: "Approved (Days)",
        pendingCount: "Pending (Count)",
        pendingDays: "Pending (Days)",
        rejectedCount: "Rejected (Count)",
        rejectedDays: "Rejected (Days)",
        leaveType: "Leave Type",
        daysCount: "Number of Days",
        emergencyContactName: "Emergency Contact - Name",
        emergencyContactPhone: "Emergency Contact - Phone",
        daysRemaining: "Days Remaining",
        age: "Age",
        daysInProbation: "Days in Probation",
        completed: "Completed",
        date: "Date",
        checkIn: "Check In",
        checkOut: "Check Out",
        workHours: "Working Hours",
        overtimeHours: "Overtime Hours",
        earlyLeave: "Early Leave",
        deductionAmount: "Deduction Amount",
        totalActions: "Total Actions",
        absences: "Absences",
        lateArrivals: "Late Arrivals",
        warnings: "Warnings",
        penalties: "Penalties",
        type: "Type",
        reason: "Reason",
        description: "Description",
        penaltyAmount: "Penalty Amount",
        bankName: "Bank Name",
        accountNumber: "Account Number",
        iban: "IBAN",
        swiftCode: "Swift Code",
        paymentMethod: "Payment Method",
        departureDate: "Departure Date",
        requestId: "Request ID",
        requestDate: "Request Date",
        duration: "Duration (Days)",
        approvedBy: "Approved By",
        rejectionReason: "Rejection Reason",
        totalEmployees: "Total Employees",
        activeEmployees: "Active Employees",
        onLeave: "On Leave",
        departmentsCount: "Number of Departments",
        totalBaseSalaries: "Total Base Salaries",
        averageSalary: "Average Salary",
        maxSalary: "Highest Salary",
        minSalary: "Lowest Salary",
        totalAllowances: "Total Allowances",
        totalDeductions: "Total Deductions",
        employeesCount: "Number of Employees",
        approvedCountStat: "Approved",
        underReview: "Under Review",
        teamLeaders: "Team Leaders",
        totalApprovedLeaves: "Total Approved Leaves",
        totalPendingLeaves: "Total Pending Leaves",
        totalRejectedLeaves: "Total Rejected Leaves",
        totalLeaveBalance: "Total Leave Balance",
        averageLeaveBalance: "Average Leave Balance",
        totalLeaveRequests: "Total Leave Requests",
        totalApprovedLeaveDays: "Total Approved Leave Days",
        totalPendingLeaveDays: "Total Pending Leave Days",
        totalRejectedLeaveDays: "Total Rejected Leave Days",
        totalAllLeaveDays: "Total All Leave Days",
        employeesWithEmail: "Employees with Email",
        employeesWithPhone: "Employees with Phone",
        employeesWithEmergencyContact: "Employees with Emergency Contact",
        totalBirthdays: "Total Birthdays",
        probationCompleted: "Probation Completed",
        inProbation: "In Probation",
        averageDaysRemaining: "Average Days Remaining",
        totalDisciplinaryActions: "Total Disciplinary Actions",
        totalWarnings: "Total Warnings",
        totalPenalties: "Total Penalties",
        totalAbsences: "Total Absences",
        totalLateArrivals: "Total Late Arrivals",
        employeesWithActions: "Employees with Disciplinary Actions",
        totalPenaltyAmount: "Total Penalty Amount",
        employeesWithBankAccount: "Employees with Bank Account",
        employeesWithIBAN: "Employees with IBAN",
        employeesWithSwiftCode: "Employees with Swift Code",
        totalFormerEmployees: "Total Former Employees",
        totalRequests: "Total Requests",
        totalPermissionDays: "Total Permission Days"
      },
      fileNames: {
        generalInfo: "general_info",
        salaryInfo: "salary_info",
        employmentInfo: "employment_info",
        leaveBalance: "leave_balance",
        annualLeaves: "annual_leaves",
        contactInfo: "contact_info",
        upcomingBirthdays: "upcoming_birthdays",
        days: "days",
        probationPeriods: "probation_periods",
        attendanceData: "attendance_data",
        disciplinaryReport: "disciplinary_report",
        bankAccounts: "bank_accounts",
        formerEmployees: "former_employees",
        permissionRequests: "permission_requests",
        statusAll: "all",
        statusPending: "pending",
        statusApproved: "approved",
        statusRejected: "rejected"
      }
    }
  },
  attendance: {
    title: "Attendance Record",
    description: "View attendance of all employees for a specific month",
    employeeRecord: "Attendance Record",
    loadingEmployees: "Loading employees...",
    loadingData: "Loading data...",
    noEmployees: "No employees",
    noSearchResults: "No search results",
    noAttendanceData: "No attendance data for this month",
    noAttendanceDataYet: "No attendance data available yet",
    employeesList: "Attendance Record",
    viewAttendance: "View Attendance",
    viewAttendanceDetails: "View attendance details",
    monthlyDetails: "Monthly Attendance Details",
    searchPlaceholder: "Search for employee by name, position, or department...",
    viewingMonth: "Viewing attendance for",
    clickToView: "Click \"View Attendance\" button for any employee to view their full attendance details for this month",
    stats: {
      presentDays: "Present Days",
      absentDays: "Absent Days",
      lateDays: "Late Days",
      holidayDays: "Holiday Days",
      earlyLeaveDays: "Early Leave Days",
      totalHours: "Working Hours",
      overtimeHours: "Overtime Hours"
    },
    tableHeaders: {
      number: "Number",
      name: "Name",
      position: "Position",
      department: "Department",
      actions: "Actions",
      employeeName: "Employee Name",
      date: "Date",
      checkIn: "Check In",
      checkOut: "Check Out",
      checkInLocation: "Check In Location",
      checkOutLocation: "Check Out Location",
      workingHours: "Working Hours",
      status: "Status"
    },
    status: {
      present: "Present on time",
      absent: "Absent",
      late: "Late",
      holiday: "Official Holiday",
      leave: "Paid Leave",
      unpaidLeave: "Unpaid Leave",
      halfDay: "Half Day",
      quarterDay: "Quarter Day",
      earlyLeave: "Early Leave",
      autoCheckout: "Auto Checkout",
      externalTask: "External Task",
      notSpecified: "Not Specified"
    },
    time: {
      morning: "AM",
      evening: "PM",
      hours: "hours"
    },
    payroll: {
      generateTitle: "Generate Payroll Plans for This Month",
      generateDescription: "Salary will be calculated from attendance, check-out, and deductions, and payroll will be created for the specified month",
      generating: "Generating...",
      generateButton: "Generate plans for this month",
      selectEmployeeFirst: "Please select an employee first",
      confirmGenerate: "Do you want to generate payroll for employee",
      confirmForMonth: "for month",
      success: "Payroll generated successfully for employee",
      error: "An error occurred while generating payroll",
      failed: "Failed to generate payroll"
    },
    days: {
      sunday: "Sunday",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday"
    },
    errorLoadingAttendance: "Failed to load attendance data",
    errorLoadingAttendanceMessage: "An error occurred while loading attendance data",
    errorSelectDates: "Please select start and end dates",
    errorFetchingData: "Failed to fetch data for export",
    noDataToExport: "No data to export",
    errorExporting: "An error occurred while exporting data",
    exportSuccess: "Exported",
    exportSuccessRecords: "attendance records successfully",
    employeeNumber: "Employee Number",
    employeeName: "Employee Name",
    position: "Position",
    date: "Date",
    checkInTime: "Check In Time",
    checkOutTime: "Check Out Time",
    checkInPhoto: "Check-in Photo",
    viewCheckInPhoto: "View check-in photo",
    noCheckInPhoto: "No photo",
    closePhoto: "Close photo",
    workingHours: "Working Hours",
    overtimeHours: "Overtime Hours",
    earlyLeave: "Early Leave",
    deductionAmount: "Deduction Amount",
    attendanceRecord: "Attendance Record",
    refresh: "Refresh",
    exportExcel: "Download Excel",
    exporting: "Exporting...",
    fromDate: "From Date",
    toDate: "To Date",
    userIDNotAvailable: "User ID not available",
    errorLoadingWeeklyAttendance: "Failed to load weekly attendance data",
    errorLoadingTodayAttendance: "Failed to load today's attendance data",
    attendanceData: "Attendance Data",
    selectDateRange: "Select Date Range (Optional)",
    selectDateRangeTitle: "Select Date Range",
    cancel: "Cancel",
    save: "Save",
    todayAttendance: "Today's Attendance",
    periodAttendance: "Period Attendance",
    workedFrom: "Worked from",
    workedTo: "to",
    workingHoursLabel: "Working Hours:",
    overtimeHoursLabel: "Overtime Hours:",
    deductionHoursLabel: "Deduction Hours:",
    hours: "hours",
    presentDays: "Present Days",
    absentDays: "Absent Days",
    earlyLeaveDays: "Early Leave",
    lateDays: "Late Days",
    holidayDays: "Holiday Days",
    halfDay: "Half Day",
    errorFetchingLocation: "Error fetching location",
    enableLocationPermission: "Please enable location permissions in browser",
    browserNotSupportGeolocation: "Browser does not support Geolocation API",
    mainOffice: "Main Office",
    deliveryByOrder: "Delivery by Order",
    deliveryByOrderNoCheckIn: "This work type does not require check-in or check-out",
    yourCurrentLocation: "Your Current Location",
    companyLocation: "Company Location",
    updateEmployeeLocation: "Update Employee Location",
    updateMapData: "Update Map Data",
    clearBrowserCache: "Clear Browser Cache",
    enableLocationPermissionForCheckIn: "Location permissions required to check in",
    outsideCompanyRadius: "You are outside any company branch radius.",
    enableLocationPermissionForCheckOut: "Location permissions required to check out",
    checkInSuccess: "Check-in successful",
    checkInFailed: "Check-in failed",
    errorDuringCheckIn: "An error occurred during check-in",
    checkOutSuccess: "Check-out successful",
    checkOutFailed: "Check-out failed",
    errorDuringCheckOut: "An error occurred during check-out",
    notRegistered: "Not Registered",
    registered: "Registered",
    checkedOut: "Checked Out",
    checkIn: "Check In",
    checkOut: "Check Out",
    loading: "Loading...",
    todayAttendanceTitle: "Today's Attendance",
    statistics: "Statistics",
    selectStatisticsPeriod: "Select Statistics Period",
    thisMonth: "This Month",
    lastMonth: "Last Month",
    allData: "All Data",
    startDatePlaceholder: "Start Date",
    endDatePlaceholder: "End Date",
    search: "Search",
    dailyHoursAverage: "Daily Hours Average",
    hoursPerDay: "hours per day",
    totalWorkingHours: "Total Working Hours",
    attendanceRate: "Attendance Rate",
    lastUpdate: "Last Update:",
    enter: "Enter",
    exit: "Exit",
    extra: "Extra",
    deduction: "Deduction",
    errorLoadingUserData: "Failed to load user data",
    errorLoadingDailyAttendance: "Failed to load daily attendance data",
    errorLoadingData: "An error occurred while loading data",
    and: "and",
    rejectReasonOptional: "Rejection reason (optional):",
    enterRejectionReason: "Please enter rejection reason:",
    rejectionReasonLabel: "Rejection Reason:",
    enterDeductionNameInRow: "Please enter deduction name in row",
    enterValidAmountInRow: "Please enter valid amount in row",
    enterAllowanceName: "Please enter allowance name",
    enterValidAllowanceAmount: "Please enter valid allowance amount",
    enterValidBaseSalary: "Please enter valid base salary",
    enterDailyOrdersCount: "Please enter daily orders count (must be 1 or more)",
    enterExpectedDailyOrders: "Please enter expected daily orders count for employee",
    enterBaseSalaryInEGP: "Please enter the employee base salary",
    enterEndDateOrEnablePermanent: "Please enter end date or enable \"Permanent\" option",
    enterRejectionReasonMinLength: "Rejection reason must be at least 10 characters",
    enterRejectionReasonMaxLength: "Rejection reason is too long (maximum 500 characters)",
    enterRejectionReasonForEmployee: "Please enter rejection reason for employee",
    rejectionReasonPlaceholder: "Rejection reason (between 10 and 500 characters)...",
    enterValidCoordinates: "Please enter valid coordinates",
    enterBranchName: "Please enter branch name",
    enterDepartmentName: "Please enter department name",
    enterAssetName: "Please enter asset name",
    enterValidPrice: "Please enter valid price",
    enterDocumentTitle: "Please enter document title",
    enterToolName: "Please enter tool name",
    confirmApproveTransaction: "Do you want to approve this transaction?",
    failedToApproveTransaction: "Failed to approve transaction. Please try again later.",
    failedToFetchTransactions: "Failed to fetch transactions. Please try again later."
  },
  approvals: {
    title: "Approvals",
    description: "Manage approvals for transactions and requests",
    rejectReasonOptional: "Rejection reason (optional):",
    enterRejectionReason: "Please enter rejection reason:",
    rejectionReasonLabel: "Rejection Reason:",
    confirmApproveTransaction: "Do you want to approve this transaction?",
    failedToApproveTransaction: "Failed to approve transaction. Please try again later.",
    failedToFetchTransactions: "Failed to fetch transactions. Please try again later.",
    rejectReasonForMultipleTransactions: "Rejection reason for",
    transactions: "transaction",
    rejectionReasonOptionalForMultiple: "(optional):"
  },
  common: {
    all: "All",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    edit: "Edit",
    delete: "Delete",
    create: "Create",
    update: "Update",
    search: "Search",
    searchPlaceholder: "Search in settings or departments...",
    loading: "Loading...",
    saving: "Saving...",
    error: "An error occurred",
    success: "Success",
    other: "Other",
    clearCacheConfirm: "Do you want to clear stored data and reload the page?\nThis will delete temporary data\nYou will need to log in again",
    clearCacheError: "An error occurred while clearing cache. Please try again.",
    clearCacheSuccess: "Cache cleared successfully",
    aiGreeting: "Please greet them and let them know you are an AI programmed by Hamdy Mohamed",
    unknown: "Unknown",
    notSpecified: "Not Specified",
    confirm: "Confirm",
    deletedUndo: "Deleted",
    undoSuccessful: "Undo successful",
    undo: "Undo",
    warning: "Warning",
    info: "Info",
    retry: "Retry",
    reloadPage: "Reload page",
    pageNotFoundTitle: "404 - Page not found",
    pageNotFoundMessage: "Sorry, the page you are looking for does not exist.",
    login: "Sign in",
    user: "User",
    amount: "Amount",
    amountMustBePositive: "{label} must be a positive number",
    amountMustNotExceed: "{label} must not exceed {max}",
    emptyData: "No data available",
    emptyDataMessage: "No data available to display currently",
    loadingData: "Loading data...",
    orgNotFound: "Organization not found. Please contact support.",
    routeNotFound: "Page not found",
    now: "Just now",
    agoDay: "{n} day ago",
    agoDays: "{n} days ago",
    agoHour: "{n} hour ago",
    agoHours: "{n} hours ago",
    agoMinute: "{n} minute ago",
    agoMinutes: "{n} minutes ago",
    pause: "Pause",
    resume: "Resume",
    previous: "Previous",
    next: "Next",
    read: "Read",
    unread: "Unread"
  },
  errorBoundary: {
    connectionIssue: "Connection problem",
    sessionExpired: "Session expired",
    somethingWentWrong: "Something went wrong",
    chunkError: "The page could not load after an update. Clear cache or reload once.",
    networkError: "Cannot connect to the server. Check your internet connection and try again.",
    authError: "Your login session has expired. Please sign in again.",
    unexpectedError: "Sorry, an unexpected error occurred. Please try again later.",
    errorDetails: "Error details (development only)"
  },
  layout: {
    supportSessionActive: "Active support session",
    supportSessionBy: " (by {name})",
    supportSessionSuffix: " — you are viewing the system as the selected user.",
    supportSessionEnd: "End support session"
  },
  supportSession: {
    invalidLinkNoToken: "Invalid session link (no token).",
    invalidLinkMissingToken: "Invalid session link (token missing).",
    invalidToken: "Invalid token.",
    sessionExpired: "This session link has expired. Request a new impersonation from the admin panel.",
    notSupportSession: "This link is not for a support session.",
    invalidOrgId: "Invalid organization id in the support link.",
    invalidTenantId: "Invalid tenant id in the support link.",
    openFailed: "Could not open the support session. Try again from the admin panel.",
    title: "Could not open session",
    opening: "Opening support session…",
    confirmTitle: "Start support session?",
    confirmBody: "You are about to view the app as the selected user. Continue only if you requested this from the admin panel.",
    confirmReplaceWarning: "This will replace your current signed-in session on this browser.",
    startSession: "Start support session",
    cancel: "Cancel"
  },
  infoPop: {
    title: "Exciting new feature!",
    description: "Discover the new feature that will transform your experience with us",
    actionText: "Try now"
  },
  updates: {
    newUpdate: "New update",
    close: "Close",
    pause: "Pause",
    resume: "Resume",
    pauseTitle: "Pause (Space)",
    resumeTitle: "Resume (Space)",
    clickToPause: "Click to pause (or press Space)",
    clickToResume: "Click to resume (or press Space)",
    previous: "Previous",
    next: "Next",
    read: "Read",
    unread: "Unread",
    noText: "No update text",
    video: "Video"
  },
  chat: {
    title: "Chat",
    mobileTitle: "Chat",
    searchPlaceholder: "Search...",
    noConversations: "No conversations",
    noMessages: "No messages yet. Start the conversation!",
    noMessagesPlaceholder: "No messages",
    selectConversation: "Select a conversation to start",
    typeMessage: "Type a message...",
    attachFile: "Attach file",
    attachImage: "Attach image",
    addEmoji: "Add emoji",
    sendMessage: "Send message",
    openConversations: "Open conversations list",
    now: "Now",
    minutesAgo: "ago",
    minute: "minute",
    employee: "Employee",
    department: "Department",
    minutes: "minutes",
    startConversation: "Start the conversation with your team",
    members: "member",
    groupChat: "Group Chat",
    groups: "Departments",
    selectGroup: "Select a department to start",
    deleteMessageConfirm: "Are you sure you want to delete this message?",
    delete: "Delete",
    cancel: "Cancel",
    deleteSuccess: "Message deleted successfully",
    fetchGroupsFailed: "Failed to fetch groups",
    fetchMessagesFailed: "Failed to fetch messages",
    sendMessageFailed: "Failed to send message",
    deleteMessageFailed: "Failed to delete message",
    unnamedGroup: "Unnamed group",
    today: "Today",
    yesterday: "Yesterday",
    connected: "Connected",
    disconnected: "Disconnected",
    you: "You",
    welcomeGeneral: "Welcome to General Chat!",
    welcomeDepartment: "Welcome to {name} Chat!",
    welcomeSubtitle: "Start the conversation with your team. Send a message to get started!",
    minuteShort: "m",
    reply: "Reply",
    replyingTo: "Replying to",
    cancelReply: "Cancel reply",
    react: "React",
    pin: "Pin",
    unpin: "Unpin",
    pinnedMessages: "Pinned messages",
    pinnedMessage: "Pinned message",
    searchMessages: "Search messages",
    searchMessagesPlaceholder: "Search messages...",
    noSearchResults: "No results found",
    pinSuccess: "Message pinned",
    unpinSuccess: "Message unpinned",
    pinFailed: "Failed to pin message",
    pinUnauthorized: "Only admins and team leaders can pin messages.",
    pinLimitReached: "You can only pin up to 5 messages. Unpin one to pin another.",
    reactionFailed: "Failed to add reaction",
    searchFailed: "Search failed"
  },
  sidebar: {
    title: "HR System",
    expand: "Expand",
    collapse: "Collapse",
    settings: "Settings",
    mainMenu: "Main Menu",
    hrSystem: "Human Resources Management System",
    version: "Version 1.0.0",
    dashboard: "Dashboard",
    employees: "Employees",
    payroll: "Payroll",
    salary: "Salary",
    holidays: "Holidays",
    permissions: "Permissions",
    company: "Company",
    reports: "Reports",
    attendance: "Attendance",
    tasks: "Task Manager",
    chat: "Chat",
    documents: "My Documents",
    myProfile: "My Profile",
    employeesList: "Employees Directory",
    home: "Home",
    connected: "Connected",
    searchPlaceholder: "Search in system...",
    notifications: "Notifications",
    newNotification: "new notification",
    viewUpdates: "View Updates",
    viewUpdate: "View",
    newUpdate: "new update",
    companyCode: "Company Code",
    roles: {
      employee: "Employee",
      admin: "Admin",
      teamLeader: "Team Leader",
      user: "User"
    }
  },
  topHeader: {
    searchPlaceholder: "Search in system...",
    assistBar: {
      ask: "Ask",
      askHint: "Ask where something is in the system",
      jump: "Quick jump",
      jumpHint: "Open the quick navigation list"
    },
    toggleLightMode: "Switch to light mode",
    toggleDarkMode: "Switch to dark mode",
    changeLanguage: "Change language",
    notifications: "Notifications",
    notificationsPanel: "Notifications panel",
    loading: "Loading...",
    loadError: "Load error",
    noNotifications: "No notifications to display",
    loadingNotifications: "Loading notifications...",
    loadingMore: "Loading...",
    loadMore: "Load more notifications",
    seeMore: "See more",
    seeLess: "See less",
    allRead: "All notifications read",
    justNow: "Just now",
    unreadNotification: "unread notification",
    total: "Total",
    notificationCount: "Notification count:",
    decreaseCount: "Decrease notifications per page",
    increaseCount: "Increase notifications per page",
    countPerPage: "Number of notifications per page",
    markAllAsRead: "Mark all as read",
    deleteAll: "Delete all",
    closeWindow: "Close window",
    noNotificationsTitle: "No notifications at the moment",
    noNotificationsMessage: "You will be notified when new notifications arrive",
    notificationWithoutTitle: "Notification without title",
    noNotificationText: "No notification text",
    unreadNotificationLabel: "Unread notification",
    notificationLabel: "Notification:",
    fetchFailed: "Failed to fetch notifications",
    fetchError: "An error occurred while fetching notifications",
    fetchNewFailed: "Failed to fetch new notifications. Retrying...",
    markReadFailed: "Failed to update notification status",
    markAllReadFailed: "Failed to update notifications status",
    deleteFailed: "Failed to delete notification",
    deleteAllFailed: "Failed to delete all notifications",
    unauthorized: "Unauthorized, please check token",
    noTitle: "No title",
    noMessage: "No message",
    read: "Read",
    unread: "Unread",
    markAsRead: "Mark as read",
    delete: "Delete",
    viewAllNotifications: "View all notifications",
    userMenu: "User Menu",
    profile: "Profile",
    settings: "Settings",
    logout: "Logout",
    login: "Login",
    user: "User",
    admin: "System Administrator",
    teamLeader: "Team Leader",
    employee: "Employee",
    newNotification: "New notification",
    youHaveNewNotification: "You have a new notification",
    browserNotSupportNotifications: "This browser does not support notifications.",
    notificationPermissionDenied: "Notification permission was denied. Please enable notifications in your browser settings.",
    notificationPermissionError: "An error occurred while requesting notification permission.",
    searchPopup: {
      title: "Ask TeamIN",
      description: "Find any page or feature in a few words",
      placeholder: "What are you looking for?",
      closeHint: "to close",
      sendHint: "to send",
      sending: "Looking it up...",
      fullScreen: "Full screen",
      exitFullScreen: "Exit full screen",
      welcomeMessage: "Hi! Ask me where something is — for example: “Where is payroll?” or “Company settings”",
      welcomeMessageEmployee: "Hi! Ask me where something is — for example: “Where is my salary?” or “How do I request leave?”",
      responseMessage: "Something went wrong. Please try again.",
      takeMeThere: "Take me there",
      suggestion1: "Where is payroll?",
      suggestion2: "Company settings",
      suggestion3: "Attendance reports",
      suggestion1Employee: "Where is my salary?",
      suggestion2Employee: "How do I request leave?",
      suggestion3Employee: "My documents"
    },
    report: {
      title: "Report a problem or suggestion",
      description: "Pick the area that matches your issue and write a short description. We'll review it soon.",
      subject: "Subject",
      subjectPlaceholder: "e.g. Check-in failed because of location",
      category: "What is this about?",
      categoryHint: "Choose the closest area so we can route your report correctly.",
      categories: {
        attendance: "Attendance & check-in",
        notifications: "Notifications",
        payroll: "Payroll & payslips",
        leaves_permissions: "Leaves & permissions",
        tasks_chat: "Tasks & chat",
        account_login: "Login, account & device",
        suggestion: "Improvement idea",
        other: "Something else",
        technical: "Technical",
        billing: "Billing",
        account: "Account",
        feature_request: "Feature request"
      },
      descriptionLabel: "Description",
      placeholder: "What happened, when, and any error message you saw...",
      submit: "Submit",
      cancel: "Cancel",
      close: "Close",
      sending: "Sending...",
      thankYou: "Thank you. Your message has been received successfully.",
      errorSending: "Something went wrong while sending. Please try again."
    }
  },
  forgetPassword: {
    title: "Reset Password",
    titleStep2: "Enter Code and New Password",
    description: "Enter your email to receive a reset code",
    descriptionStep2: "Enter the code sent to your email and your new password",
    email: "Email",
    emailPlaceholder: "Enter your email",
    code: "Verification Code",
    codePlaceholder: "Enter verification code",
    newPassword: "New Password",
    newPasswordPlaceholder: "Enter new password",
    sendCode: "Send Verification Code",
    sending: "Sending...",
    resetPassword: "Reset Password",
    resetting: "Resetting...",
    backToLogin: "Back to Login",
    codeSent: "Reset code has been sent to your email",
    resetSuccess: "Password has been reset successfully",
    sendError: "An error occurred while sending the code",
    resetError: "An error occurred while resetting the password",
    passwordTooShort: "Password is required",
    validationCodeLength: "Enter the 6-digit verification code"
  },
  deleteAccount: {
    title: "Delete Account",
    description:
      "Request permanent deletion of your TeamIN account and associated personal data. This page fulfills Google Play account deletion requirements.",
    verifyHint: "Sign in with your email and password to verify your identity before deletion.",
    continue: "Continue",
    verifying: "Verifying...",
    confirmDescription: "Confirm that you want to permanently delete this account and its data.",
    accountLabel: "Account",
    confirmCheckbox:
      "I understand that deleting my account will permanently remove my profile, email, and associated data, and this cannot be undone.",
    confirmCheckboxRequired: "Please confirm that you understand this action is permanent.",
    deleteButton: "Delete my account",
    deleting: "Deleting...",
    back: "Back",
    backHome: "Back to Home",
    failed: "Failed to delete account. Please try again.",
    doneTitle: "Account deleted",
    doneDescription: "Your account and associated personal data have been deleted.",
    doneNote: "You can close this page. If you use the mobile app, sign out or uninstall as needed.",
    warningTitle: "This action is permanent",
    warningItem1: "Your account login and profile information will be removed.",
    warningItem2: "Personal data tied to your user account will be deleted from our systems.",
    warningItem3: "You will no longer be able to sign in with this account.",
    appHint: "You can also delete your account from Settings in the TeamIN mobile app. Read our",
  },
  login: {
    admin: "Admin",
    employee: "Employee",
    teamLeader: "Team Leader",
    superAdmin: "Super Admin",
    expired: "Expired",
    sessionExpired: "Session expired, please login again",
    invalidToken: "No valid token returned",
    error: "An error occurred during login",
    toggleTheme: "Toggle Theme",
    switchToLight: "Switch to light mode",
    switchToDark: "Switch to dark mode",
    title: "Login",
    subtitle: "Enter your credentials to access your account",
    username: "Email",
    usernamePlaceholder: "Enter your email",
    password: "Password",
    passwordPlaceholder: "Enter password",
    forgotPassword: "Forgot password?",
    submitting: "Logging in...",
    submit: "Login",
    noAccount: "Don't have an account?",
    createAccountAlert: "You must install the Teamin app from the store to create a new account",
    createAccount: "Create Account",
    copyright: "All rights reserved.",
    privacyPolicy: "Privacy Policy",
    selectOrg: "Select Organization",
    selectOrgDesc: "You have accounts in multiple organizations. Please select the organization you want to log in to",
    rateLimitExceeded: "You have tried to log in too many times. Please wait a moment and try again.",
    deviceNotRegistered: "This device is not registered to your account. Please contact an administrator to activate the device.",
    accountLocked: "Login is temporarily suspended due to several failed attempts. Please try again in a few minutes.",
    heroTitle: "Human Resources Management System",
    heroSubtitle: "A comprehensive platform for managing employees, payroll, and financial operations",
    features: {
      employees: {
        title: "Employee Management",
        description: "Easily add and update employee data"
      },
      payroll: {
        title: "Payroll Management",
        description: "Calculate and track salaries and allowances"
      },
      reports: {
        title: "Financial Reports",
        description: "Comprehensive reports on financial status"
      },
      attendance: {
        title: "Attendance Management",
        description: "Track employee attendance and absences"
      }
    }
  },
  landing: {
    footer: {
      allRightsReserved: "All rights reserved.",
      portfolio: "Portfolio"
    },
    marquee: {
      label: "Trusted by teams worldwide"
    }
  },
  smartSearch: {
    placeholder: "Type to jump anywhere...",
    close: "Close",
    navigate: "Navigate",
    select: "Select",
    noResults: "No Results",
    noResultsDescription: "Try different search terms",
    selected: "Selected",
    footer: {
      assistant: "Quick navigation · Guides · UI labels · Ctrl/⌘ K",
      results: "Results"
    },
    categories: {
      route: "Pages",
      guide: "Guides",
      i18n: "From interface labels"
    },
    routes: {
      dashboard: "Dashboard",
      "dashboard.description": "View system overview",
      employees: "Employees",
      "employees.description": "Manage employees",
      payroll: "Payroll",
      "payroll.description": "Manage payroll",
      settings: "Settings",
      "settings.description": "System settings",
      "system-logs": "System Logs",
      "system-logs.description": "View system activity logs",
      reports: "Reports",
      "reports.description": "View reports and analytics",
      company: "Company",
      "company.description": "Company information and branches",
      notifications: "Notifications",
      "notifications.description": "View all notifications",
      tasks: "Tasks",
      "tasks.description": "Manage tasks and projects",
      chat: "Chat",
      "chat.description": "Team messaging",
      attendance: "Attendance",
      "attendance.description": "Attendance records",
      holidays: "Holidays",
      "holidays.description": "Leave requests and tracking",
      permissions: "Permissions",
      "permissions.description": "Manage roles and permissions",
      "employees-list": "Employees Directory",
      "employees-list.description": "Browse the employees directory",
      salary: "Salary",
      "salary.description": "View salary information",
      me: "My Profile",
      "me.description": "Your personal profile",
      overview: "Overview",
      "overview.description": "Profile overview",
      "desktop-tracking": "Desktop Tracking",
      "desktop-tracking.description": "Desktop activity tracking",
      Msg: "Messages",
      "Msg.description": "Your messages",
      performance: "Performance",
      "performance.description": "Performance evaluation",
      myDocument: "My Documents",
      "myDocument.description": "Your documents and files",
      requests: "Requests",
      "requests.description": "Your requests"
    },
    help: {
      dashboard: {
        title: "Dashboard",
        description: "View system overview"
      },
      employees: {
        title: "Employees",
        description: "Manage employees"
      },
      attendance: {
        title: "Attendance",
        description: "Attendance record"
      },
      payroll: {
        title: "Payroll",
        description: "Manage payroll"
      }
    }
  },
  hero: {
    badge: "Complete HR Management Solution",
    title: "TeamIN",
    subtitle: "HR System",
    description: "HR Management in Your Pocket - From Employees to Admins",
    joinWaitingList: "Join Waiting List",
    learnMore: "Learn More",
    poweredBy: "The system powered by AI",
    downloadApp: "Download the App",
    appStore: "App Store",
    googlePlay: "Google Play",
    downloadOn: "Download on the",
    getItOn: "Get it on",
    secureEncrypted: "Secure & Encrypted",
    cloudBased: "Fully Cloud-Based",
    aiPowered: "AI-Powered"
  },
  features: {
    title: "Features",
    subtitle: "Complete HR management solution for employees and administrators.",
    eyebrow: "A growing system for",
    bentoSubtitle: "The modules teams actually use — attendance, payroll, chat, and more in one place.",
    stats: {
      modules: "Attendance & payroll",
      platforms: "Chat & tasks",
      featuresCount: "in one system"
    },
    allFeatures: "Key Features",
    categories: {
      employeeManagement: "Employee Management",
      attendance: "Attendance & Time Tracking",
      payroll: "Payroll & Finance",
      communication: "Communication & Collaboration",
      tasks: "Tasks & Projects",
      reports: "Reports & Analytics",
      admin: "Administration & Settings"
    },
    employeeManagement: {
      title: "Employee Management",
      description: "Comprehensive employee data management"
    },
    teamLeader: {
      title: "Team Leader",
      description: "Team and project management"
    },
    documents: {
      title: "Documents",
      description: "Document and file management"
    },
    performance: {
      title: "Performance",
      description: "Employee performance evaluation"
    },
    companyManagement: {
      title: "Company Management",
      description: "Company and branch data management"
    },
    clientsManagement: {
      title: "Clients Management",
      description: "Client data management"
    },
    checkInOut: {
      title: "Check In/Out",
      description: "Advanced check-in/out system"
    },
    deviceBinding: {
      title: "Device Binding",
      description: "Secure device binding"
    },
    timeAttendance: {
      title: "Time Attendance",
      description: "Accurate attendance tracking"
    },
    payslip: {
      title: "Payslip",
      description: "Generate payslips"
    },
    payrollManagement: {
      title: "Payroll Management",
      description: "Calculate and manage payroll"
    },
    transactions: {
      title: "Transactions",
      description: "Track financial transactions"
    },
    groupChat: {
      title: "Group Chat",
      description: "Communicate with your team"
    },
    realtimeChat: {
      title: "Real-time Chat",
      description: "Instant messaging with employees"
    },
    aiChatbot: {
      title: "AI Chatbot",
      description: "Smart assistant for answering queries"
    },
    notifications: {
      title: "Notifications",
      description: "Advanced notification system"
    },
    tasksManagement: {
      title: "Tasks Management",
      description: "Create and track tasks"
    },
    advancedAnalytics: {
      title: "Advanced Analytics",
      description: "Comprehensive data analytics"
    },
    reportsAnalytics: {
      title: "Reports & Analytics",
      description: "Detailed reports and statistics"
    },
    adminDashboard: {
      title: "Admin Dashboard",
      description: "Comprehensive admin dashboard"
    },
    holidaysManagement: {
      title: "Holidays Management",
      description: "Manage holidays and requests"
    },
    approvals: {
      title: "Approvals",
      description: "Approval and authorization system"
    },
    categoriesManagement: {
      title: "Categories Management",
      description: "Manage company departments"
    },
    desktopTracking: {
      title: "Desktop Tracking",
      description: "Track employee activity"
    },
    mobileApp: {
      title: "Mobile App",
      description: "Mobile application"
    }
  },
  benefits: {
    title: "Why Choose",
    titleTeamIN: "TeamIN",
    titleQuestion: "?",
    eyebrow: "Why teams switch",
    description: "The comprehensive HR management system that brings together everything you need to manage your workforce efficiently and effectively.",
    getStarted: "Get Started",
    benefit1: "Comprehensive employee management from hiring to retirement",
    benefit2: "Real-time attendance tracking with location tracking",
    benefit3: "Automated payroll calculation with bonuses and deductions",
    benefit4: "Seamless communication through group chat",
    benefit5: "Detailed reports and analytics for informed decision-making",
    benefit6: "High security with device binding and data protection",
    benefit7: "Advanced AI for data analytics and smart predictions",
    chat: {
      groupName: "HR Team",
      online: "12 online",
      members: "24 members",
      typing: "Sara is typing...",
      placeholder: "Type a message...",
      avatarInitials: "SA"
    },
    aiPowered: "AI-Powered"
  },
  contact: {
    title: "Join the",
    waitingList: "Waiting List",
    eyebrow: "Get early access",
    description: "Be among the first to get TeamIN HR System. Register your email and we will notify you when we are ready!",
    contactUs: "Contact Us",
    viewPortfolio: "View Portfolio"
  },
  privacy: {
    title: "Privacy Policy",
    paragraph1: "We are committed to protecting your privacy. Your personal information will not be shared with any third party.",
    paragraph2: "If you have any questions about our privacy policy, please contact us at",
    close: "Close",
    readFull: "Read full privacy policy"
  },
  form: {
    emailLabel: "Email Address",
    emailPlaceholder: "Enter your email",
    phoneLabel: "Phone Number",
    phonePlaceholder: "Enter your phone number",
    categoryLabel: "Company Category",
    categoryPlaceholder: "Select company category",
    success: "Successfully registered! We will contact you soon.",
    viewPortfolio: "View",
    back: "Back",
    next: "Next",
    joining: "Joining...",
    joinWaitingList: "Join Waiting List",
    privacyPolicy: "Privacy Policy"
  },
  capabilities: {
    title: "System Capabilities",
    subtitle: "A powerful and flexible system designed to grow with your business",
    eyebrow: "Built to scale",
    scrollHint: "Scroll to explore",
    multiPlatform: {
      title: "Multi-Platform",
      description: "Works on Android, iOS, and Web - full access from any device"
    },
    realTimeSync: {
      title: "Real-Time Sync",
      description: "Instant synchronization of all data across all devices"
    },
    secure: {
      title: "Secure & Protected",
      description: "Advanced protection for your data with strong encryption"
    },
    scalable: {
      title: "Scalable",
      description: "Grows with your business from small company to large enterprise"
    },
    customizable: {
      title: "Customizable",
      description: "Customize the system to fit your company's unique needs",
      modules: {
        attendance: "Attendance",
        chat: "Chat",
        tasks: "Tasks",
        reports: "Reports"
      }
    },
    support: {
      title: "Technical Support",
      description: "Dedicated support team available to help you anytime"
    },
    platforms: {
      androidIos: {
        title: "Mobile Apps",
        description: "Native apps for Android and iOS"
      },
      webDashboard: {
        title: "Web Dashboard",
        description: "Powerful dashboard for administrators"
      },
      realTimeSync: {
        title: "Real-Time Sync",
        description: "Real-time sync across all platforms"
      }
    }
  },
  screenshots: {
    title: "See TeamIN in Action",
    subtitle: "Explore our system through real screenshots from the mobile app and web dashboard",
    mobile: "Mobile App",
    web: "Web Dashboard",
    viewFullSize: "View Full Size",
    items: {
      mobileDashboard: {
        title: "Mobile Dashboard",
        description: "Beautiful and intuitive mobile dashboard for employees"
      },
      mobileAttendance: {
        title: "Check In/Out",
        description: "Easy check-in and check-out with location tracking"
      },
      mobileTasks: {
        title: "Tasks Management",
        description: "Manage and track your tasks on the go"
      },
      mobileChat: {
        title: "Team Chat",
        description: "Real-time messaging with your team"
      },
      mobilePayslip: {
        title: "Digital Payslip",
        description: "View and download your monthly payslips"
      },
      adminDashboard: {
        title: "Admin Dashboard",
        description: "Comprehensive admin dashboard with analytics"
      },
      employeeManagement: {
        title: "Employee Management",
        description: "Complete employee management system"
      },
      payrollManagement: {
        title: "Payroll Management",
        description: "Automated payroll calculation and management"
      },
      attendanceTracking: {
        title: "Attendance Tracking",
        description: "Real-time attendance monitoring and reports"
      },
      reportsAnalytics: {
        title: "Reports & Analytics",
        description: "Comprehensive reporting and analytics dashboard"
      }
    },
    placeholder: "Screenshot placeholder",
    placeholderHint: "Screenshot placeholder - Add actual screenshot images"
  },
  tasks: {
    title: "Task Manager",
    description: "Manage and track tasks",
    addTask: "Add Task",
    addTaskButton: "Add",
    taskTitle: "Task Title",
    enterTaskTitle: "Enter task title",
    enterTaskTitleRequired: "Please enter task title",
    taskDescription: "Task Description",
    enterTaskDescription: "Enter task description",
    assignType: "Assignment Type",
    singleEmployee: "Single Employee",
    fullTeam: "Full Team",
    selectEmployee: "Select Employee",
    selectEmployeeRequired: "Please select an employee",
    selectEmployeePlaceholder: "Select employee...",
    selectTeam: "Select Team",
    selectTeamPlaceholder: "Select department...",
    selectTeamRequired: "Please select a department",
    deadline: "Deadline",
    selectDeadlineRequired: "Please select deadline",
    deadlineLabel: "Deadline",
    priority: "Priority",
    high: "High",
    medium: "Medium",
    low: "Low",
    allPriorities: "All Priorities",
    status: "Status",
    pending: "Pending",
    inProgress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled",
    allStatuses: "All Statuses",
    employee: "Employee",
    team: "Team",
    expired: "Expired",
    createdAt: "Created At",
    completedAt: "Completed At",
    updates: "Updates",
    viewDetails: "View Details",
    taskDetails: "Task Details",
    task: "Task",
    noTasks: "No tasks",
    noTasksDesc: "Start by adding a new task",
    loading: "Loading...",
    search: "Search",
    searchPlaceholder: "Search in title and description...",
    clearFilters: "Clear Filters",
    updateMessage: "Update/Comment",
    enterUpdateMessage: "Enter update message...",
    addUpdate: "Add Update/Comment",
    editTask: "Edit Task",
    edit: "Edit",
    delete: "Delete",
    deleteTaskConfirm: "Are you sure you want to delete this task?",
    markDone: "Mark as Done",
    markPending: "Mark as Pending",
    cannotUpdateCompleted: "Cannot add updates to completed tasks",
    notSpecified: "Not Specified",
    untitled: "Untitled task",
    requestExtension: "Request Time Extension",
    extensionReason: "Extension Reason",
    extensionReasonPlaceholder: "Enter reason for time extension request...",
    extensionRequests: "Extension Requests",
    extensionRequestsDesc: "Review and respond to time extension requests from employees",
    requestMessage: "Request Message",
    responseMessage: "Response Message",
    respondedAt: "Response Date",
    sendRequest: "Send Request",
    sendUpdate: "Send",
    updateMessagePlaceholder: "Enter update or comment on the task...",
    noExtensionRequests: "No extension requests",
    approve: "Approve",
    reject: "Reject",
    allTasks: "All Tasks",
    all: "All",
    approved: "Approved",
    rejected: "Rejected",
    department: "Department",
    extensionReasonRequired: "Please enter extension reason",
    updateMessageRequired: "Please enter update or comment",
    requestDate: "Request Date",
    refresh: "Refresh",
    page: "Page",
    of: "of",
    tasks: "tasks",
    requests: "requests",
    previous: "Previous",
    next: "Next",
    createSuccess: "Task created successfully",
    updateSuccess: "Task updated successfully",
    deleteSuccess: "Task deleted successfully",
    markDoneSuccess: "Task marked as completed successfully",
    extensionRequestSuccess: "Extension request sent successfully",
    addUpdateSuccess: "Update added successfully",
    approveExtensionSuccess: "Extension request approved successfully",
    rejectExtensionSuccess: "Extension request rejected successfully",
    errorFetchingEmployees: "An error occurred while fetching employees",
    errorFetchingTasks: "An error occurred while fetching tasks",
    errorFetchingTaskDetails: "An error occurred while fetching task details",
    errorCreatingTask: "An error occurred while creating task",
    errorUpdatingTask: "An error occurred while updating task",
    errorDeletingTask: "An error occurred while deleting task",
    markDoneFailed: "Failed to mark task as completed",
    extensionRequestFailed: "Failed to send extension request",
    addUpdateFailed: "Failed to add update",
    approveExtensionFailed: "Failed to approve request",
    rejectExtensionFailed: "Failed to reject request",
    errorFetchingExtensionRequests: "Failed to fetch extension requests"
  },
  payroll: {
    title: "Payroll Management System",
    description: "Comprehensive management of employee salaries and financial periods",
    totalSalaries: "Total Salaries",
    beforeDeductions: "Before deductions and bonuses",
    allBasicSalaries: "All basic salaries",
    beforeModifications: "Before any modifications",
    remainingSalaries: "Remaining Salaries",
    afterDeductions: "After deductions and bonuses",
    forDisplayedEmployees: "For displayed employees",
    readyForPayment: "Ready for payment",
    bonuses: "Bonuses",
    bonusesThisMonth: "This month's bonuses",
    totalBonuses: "Total bonuses",
    forMonth: "For",
    deductions: "Deductions",
    deductionsThisMonth: "This month's deductions",
    totalDeductions: "Total deductions",
    monthlySummary: "Monthly Salary Summary",
    monthlySummaryDesc: "View salary details for a specific month",
    showSummary: "Show Summary",
    hideSummary: "Hide Summary",
    backToEmployees: "Back to Employees",
    loading: "Loading data...",
    adjustments: "Adjustments",
    adjustmentsDesc: "Partial payment (advance), salary adjustments, deduction, bonus, salary payment",
    stats: "Statistics",
    statsDesc: "Detailed payroll analytics",
    categoryAdjustments: "Adjustments",
    categoryAnalytics: "Analytics",
    tabs: {
      payments: "Payments",
      adjustments: "Adjustments (Bonuses/Deductions)"
    },
    adjustmentsPage: {
      title: "Payroll & Adjustments Management",
      employeeLabel: "Employee: {name}",
      viewDetails: "View details",
      selectEmployee: "Please select an employee",
      fillRequiredFields: "Please fill in all required fields",
      fillRequiredAmount: "Please fill in all required fields (amount must be greater than 0)",
      fullPaymentTitle: "Full salary payment",
      partialPaymentTitle: "Partial payment (advance)",
      month: "Month",
      monthPlaceholder: "YYYY-MM (e.g. 2025-01)",
      paymentMethod: "Payment method",
      bankTransfer: "Bank transfer",
      cash: "Cash",
      referenceNumberOptional: "Reference number (optional)",
      referenceNumber: "Reference number",
      notesOptional: "Notes (optional)",
      notesPlaceholder: "Any additional notes",
      earlyPayment: "Early payment",
      earlyPaymentReason: "Early payment reason",
      earlyPaymentReasonPlaceholder: "Early payment reason",
      confirmFullPayment: "Confirm salary payment",
      amountRequired: "Amount *",
      amountPlaceholder: "Enter amount",
      partialReasonRequired: "Partial payment reason *",
      partialReasonPlaceholder: "e.g. Emergency advance",
      confirmPartialPayment: "Confirm partial payment",
      addBonus: "Add bonus",
      addDeduction: "Add deduction",
      adjustmentType: "Adjustment type",
      bonusOption: "Bonus (+)",
      deductionOption: "Deduction (-)",
      bonusTypeRequired: "Bonus type *",
      deductionTypeRequired: "Deduction type *",
      bonusTypePlaceholder: "e.g. Work performance",
      deductionTypePlaceholder: "e.g. Lateness",
      amountMustBePositive: "Amount * (must be greater than 0)",
      descriptionRequired: "Description *",
      descriptionPlaceholder: "e.g. Bonus for dedication at work",
      submitBonus: "Add bonus",
      submitDeduction: "Add deduction",
      adjustmentSuccess: "Adjustment added successfully",
      adjustmentFailed: "Failed to add adjustment",
      adjustmentFailedWithError: "Failed to add adjustment: {error}",
      paySuccess: "Salary paid successfully",
      payFailed: "Failed to pay salary",
      payFailedWithError: "Failed to pay salary: {error}",
      partialPaySuccess: "Partial payment completed successfully",
      partialPayFailed: "Failed to process partial payment",
      fetchEmployeeFailed: "Failed to fetch employee data",
      fetchEmployeeFailedWithError: "Failed to fetch employee data: {error}",
      employeeDataTitle: "Employee data: {name}",
      close: "Close",
      bonuses: "Bonuses",
      noBonuses: "No bonuses",
      deductions: "Deductions",
      noDeductions: "No deductions",
      monthlyPayments: "Monthly payments",
      monthPrefix: "Month: {month}",
      netSalary: "Net salary: {amount}",
      paid: "Paid: {amount}",
      remaining: "Remaining: {amount}",
      statusCompleted: "Completed",
      statusPending: "Pending"
    },
    labels: {
      basicSalary: "Basic Salary",
      bonusesIncentivesAllowances: "Bonuses, Incentives & Allowances",
      allowances: "Allowances",
      totalRevenue: "Total Revenue",
      deductions: "Deductions",
      deductionsLabel: "Deductions",
      latePenalties: "Late Penalties",
      totalDeductions: "Total Deductions",
      netSalary: "Net Salary",
      totalEmployees: "Total Employees",
      totalGrossSalaries: "Total Gross Salaries",
      totalNetSalaries: "Total Net Salaries",
      totalDeductionsSummary: "Total Deductions",
      totalPaid: "Total Paid",
      totalRemaining: "Total Remaining",
      workDays: "Work Days",
      workHours: "Work Hours",
      timezone: "Timezone",
      revenue: "Revenue"
    },
    currentPeriod: "Edit Current Period",
    currentPeriodDesc: "Manage and calculate current month salaries",
    previousPeriods: "Previous Periods",
    previousPeriodsDesc: "View and edit previous period salaries",
    previousPeriodsViewDesc: "View previous period salaries",
    viewRecords: "View Records",
    taxSettings: "Tax and Insurance Settings",
    taxSettingsDesc: "Manage tax and social insurance settings",
    taxSettingsViewDesc: "View and manage all employee deductions",
    editSettings: "Edit Settings",
    back: "Back",
    downloadPayrollFile: "Download Payroll File",
    markAllAsPaid: "Mark All as Paid",
    deletePayroll: "Delete Payroll Calculation",
    calculateSalary: "Calculate Salary",
    calculating: "Calculating...",
    processing: "Processing...",
    markAsPaid: "Mark as Paid",
    downloadPayslip: "Download Payslip",
    revenue: "Revenue",
    bankInformation: "Bank Information",
    bankName: "Bank Name",
    accountNumber: "Account Number",
    swiftCode: "SWIFT Code",
    selectMonth: "Select Month",
    selectMonthPlaceholder: "-- Select Month --",
    selectMonthToView: "Please select a month to view payroll data",
    noPayrollData: "No payroll data for this month. Click \"Calculate Salary\" to start calculation.",
    noPayrollDataForMonth: "No payroll data for this month.",
    currency: "",
    notSpecified: "Not Specified",
    notAvailable: "Not Available",
    row: "Row",
    filtersAndSearch: "Filters and Search",
    hide: "Hide",
    showFilters: "Show Filters",
    search: "Search...",
    allDepartments: "All Departments",
    allEmployees: "All Employees",
    withDeductions: "With Deductions",
    withoutDeductions: "Without Deductions",
    noSort: "No Sort",
    highestDeductions: "Highest Deductions",
    lowestDeductions: "Lowest Deductions",
    nameAsc: "Name (A-Z)",
    nameDesc: "Name (Z-A)",
    sortByDepartment: "By Department",
    minAmount: "Min Amount",
    maxAmount: "Max Amount",
    noLimit: "No Limit",
    clearAllFilters: "Clear All Filters",
    noEmployeesAvailable: "No employees available",
    noSearchResults: "No search results",
    edit: "Edit",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    delete: "Delete",
    deductionName: "Deduction Name",
    amount: "Amount",
    endDate: "End Date",
    addDeduction: "Add Deduction",
    addNewDeduction: "Add New Deduction",
    noDeductionsDefined: "No deductions defined",
    showing: "Showing",
    to: "to",
    of: "of",
    employees: "employee",
    previous: "Previous",
    next: "Next",
    page: "Page",
    itemsPerPage: "Items Per Page",
    deductionTypes: {
      socialInsurance: "Social Insurance",
      insurance: "Insurance",
      tax: "Tax",
      loans: "Loans",
      absence: "Absence"
    },
    status: {
      paid: "Paid",
      pending: "Pending",
      partiallyPaid: "Partially Paid"
    },
    export: {
      employeeName: "Employee Name",
      baseSalary: "Base Salary",
      bonuses: "Bonuses",
      allowances: "Allowances",
      deductions: "Deductions",
      grossSalary: "Gross Salary",
      netSalary: "Net Salary",
      status: "Status",
      sheetName: "Employee Salaries",
      fileNamePrefix: "Payroll"
    },
    saveDeductionsSuccess: "Deductions saved successfully",
    downloadPayrollFileSuccess: "Payroll file downloaded successfully",
    downloadPayslipSuccess: "Payslip downloaded successfully",
    markAsPaidSuccess: "Salary marked as paid successfully",
    payAllEmployeesSuccess: "All salaries marked as paid successfully",
    calculatePayrollSuccess: "Payroll calculated successfully",
    deletePayrollSuccess: "Payroll calculation deleted successfully",
    errorFetchingPayroll: "Failed to fetch payroll data",
    errorFetchingEmployees: "Failed to load employee data",
    errorDeductionNameRequired: "Please enter deduction name in",
    errorDeductionAmountRequired: "Please enter valid amount in",
    saveDeductionsFailed: "Failed to save deductions",
    errorSavingDeductions: "An error occurred while saving deductions",
    downloadPayrollFileFailed: "Failed to download payroll file",
    downloadPayslipFailed: "Failed to download payslip",
    errorSelectMonth: "Please select a month",
    errorEmployeeIdNotAvailable: "Employee ID not available",
    errorPayrollDataRequired: "Please ensure payroll data and month are available",
    markAsPaidFailed: "Failed to mark salary as paid",
    payAllEmployeesFailed: "Failed to mark all salaries as paid",
    calculatePayrollFailed: "Failed to calculate payroll",
    calculatingPayroll: "Calculating payroll...",
    deletePayrollConfirm: "Are you sure you want to delete the payroll calculation for this month? All calculated data will be deleted.",
    payAllEmployeesConfirm: "Are you sure you want to mark all employees as paid? All employee salaries for this month will be marked as paid.",
    bulkPaymentNote: "Bulk Payment",
    byMonth: {
      searchTitle: "Search for a specific month",
      year: "Year",
      monthOptional: "Month (optional)",
      summaryTitle: "Payroll summary {year}",
      employees: "Employees",
      totalSalaries: "Total salaries",
      remaining: "Remaining",
      paid: "Paid",
      pending: "Pending",
      paidCount: "Paid",
      viewEmployeeDetails: "View specific employee details",
      selectEmployeeOptional: "Select an employee (optional)",
      employeePayrollDetails: "Payroll details: {name}",
      statusCompleted: "Completed",
      statusPartial: "Partial",
      statusPending: "Pending",
      net: "Net:",
      paidLabel: "Paid:",
      remainingLabel: "Remaining:",
      noPayrollData: "No payroll data available",
      employeeNotFound: "No data found for this employee",
      noDataHint: "No data available. Please select the year and month, then click \"Search\"",
      fetchFailed: "Failed to fetch data"
    }
  },
  systemLogs: {
    title: "System Logs",
    description: "View and manage system activity logs",
    searchPlaceholder: "Search logs...",
    exportSuccess: "Logs exported successfully",
    exportFailed: "Failed to export logs",
    clearConfirm: "Are you sure you want to delete all logs? This action cannot be undone.",
    clearSuccess: "All logs deleted successfully",
    clearFailed: "Failed to delete logs",
    fetchFailed: "Failed to fetch logs",
    statsFailed: "Failed to fetch statistics",
    noLogs: "No logs found",
    action: "Action",
    resource: "Resource",
    dateFrom: "From Date",
    dateTo: "To Date",
    clearFilters: "Clear Filters",
    export: "Export",
    clearAll: "Clear All",
    totalLogs: "Total logs",
    financialTransactions: "Financial transactions",
    today: "Today",
    thisMonth: "This month",
    searchFilters: "Search filters",
    showFilters: "Show filters",
    hideFilters: "Hide filters",
    search: "Search",
    allActions: "All actions",
    allResources: "All resources",
    logsCount: "Logs ({count})",
    pageOf: "Page {current} of {pages}",
    datetime: "Date & time",
    user: "User",
    operation: "Action",
    financialDetails: "Financial details",
    status: "Status",
    actions: "Actions",
    details: "Details",
    logDetails: "Log details",
    unknownUser: "Unknown",
    unavailable: "Unavailable",
    ip: "IP",
    amount: "Amount",
    transactionType: "Transaction type",
    category: "Category",
    transactionDate: "Transaction date",
    descriptionLabel: "Description",
    transactionId: "Transaction ID",
    transactionStatus: "Transaction status",
    completed: "Completed",
    endpoint: "Endpoint",
    detailsLabel: "Details",
    userAgent: "User agent",
    showingRange: "Showing {from} to {to} of {total} logs",
    previous: "Previous",
    next: "Next",
    actionLabels: {
      add: "Add",
      edit: "Edit",
      delete: "Delete",
      view: "View",
      login: "Login",
      logout: "Logout"
    },
    resourceLabels: {
      financial: "Financial transactions",
      employees: "Employees",
      payroll: "Payroll",
      whatsapp: "WhatsApp",
      systemLog: "System log"
    }
  },
  invoices: {
    title: "Invoices",
    description: "Manage invoices and payments",
    addInvoice: "Add New Invoice",
    editInvoice: "Edit Invoice",
    deleteConfirm: "Are you sure you want to delete this invoice?",
    deleteSuccess: "Invoice deleted successfully",
    createSuccess: "Invoice created successfully",
    updateSuccess: "Invoice updated successfully",
    statusUpdateSuccess: "Invoice status updated successfully",
    statusUpdateFailed: "Failed to update invoice status",
    fetchFailed: "Failed to fetch data",
    noPermissionDelete: "You do not have permission to delete invoices",
    noInvoices: "No invoices found",
    client: "Client",
    amount: "Amount",
    descriptionLabel: "Description",
    dueDate: "Due Date",
    paymentMethod: "Payment Method",
    notes: "Notes",
    status: "Status",
    paid: "Paid",
    pending: "Pending",
    overdue: "Overdue",
    cancelled: "Cancelled"
  },
  me: {
    desktopNotConnected: "Desktop app is not connected",
    serverDisconnected: "Not connected to the server",
    desktopRequiredForAttendance: "Please start the desktop app first to check in/out",
    commandFailed: "Failed to send command",
    actionSuccess: "completed successfully",
    remoteWorkStarted: "Remote work started",
    remoteWorkEnded: "Remote work ended",
    remoteWorkResumed: "Remote work resumed",
    remoteWorkPaused: "Remote work paused",
    breakEnded: "Break ended",
    breakStarted: "Break started",
    noReason: "No reason specified",
    monthRecordsSynced: "All month records updated from app data",
    monthRecordsSyncFailed: "Failed to update month records",
    monthRecordsSyncError: "Error updating month records",
    todayRecordsSynced: "Today's records updated successfully",
    todayRecordsSyncFailed: "Failed to update records",
    todayRecordsSyncError: "Error updating records",
    allDataSynced: "All data updated from the app",
    syncError: "An error occurred during sync",
    breakSettingsNotFound: "Break time settings not found",
    breakSettingsFetchError: "Error fetching break settings",
    employeeFetchFailed: "Failed to fetch employee data",
    employeeFetchError: "Error fetching employee data",
    holidaysFetchFailed: "Failed to fetch leave requests",
    holidaysFetchError: "Error fetching leave requests",
    weeklyAttendanceFailed: "Failed to load weekly attendance",
    weeklyAttendanceError: "Error loading attendance data",
    todayAttendanceFailed: "Failed to load today's attendance",
    todayAttendanceError: "Error loading today's attendance",
    selectMonth: "Please select a month",
    payslipDownloadSuccess: "Payslip downloaded successfully",
    noPayslipMonths: "No months with a payslip yet",
    payrollDataRequired: "Please ensure payroll data and month are available",
    markPaidSuccess: "Salary marked as paid successfully",
    documentsPage: {
      title: "My Documents",
      description: "View and manage your documents",
      fetchFailed: "Failed to fetch documents",
      noDocuments: "No documents",
      noDocumentsAvailable: "No documents available to display currently",
      noDescription: "No description",
      untitled: "Untitled document",
      unknown: "Unknown",
      view: "View",
      viewUnavailable: "View unavailable",
      download: "Download",
      successKeyword: "success",
      upload: "Upload",
      uploading: "Uploading...",
      uploadSuccess: "Document uploaded successfully",
      uploadFailed: "Failed to upload document",
      uploadToManagement: "Upload document to management",
      uploadToManagementHint: "Send a document to admin or manager",
      descriptionOptional: "Description (optional)",
      titleRequired: "Document title is required before choosing a file",
      titleRequiredPlaceholder: "Document title (required)",
      fromManagement: "From management",
      uploadedByMe: "Uploaded by me",
      noFromManagement: "No documents from management yet",
      noUploadedByMe: "You have not uploaded any documents yet"
    },
    salaryPage: {
      manageTitle: "Salary & Benefits Management",
      manageDesc: "Manage employee salary, bonuses, and deductions",
      markAsPaid: "Mark salary as paid",
      noDataForMonth: "No salary data available for this month. Please contact administration.",
      noData: "No salary data available.",
      displayError: "An error occurred while displaying salary data",
      displayErrorHint: "Please refresh the page or contact technical support",
      monthlyFixed: "Fixed monthly amount",
      monthlyBonuses: "Monthly bonuses",
      monthlyDeductions: "Monthly deductions: {amount}",
      remainingAfterPartial: "Remaining amount after partial payments",
      paidAmount: "Paid amount: {amount}",
      fromDailySalary: "From daily salary",
      dailyRate: "Daily rate (÷ work days)",
      hourlyRate: "Hourly rate (÷ 8)",
      finalNetSalary: "Final net salary",
      cash: "Cash",
      fullyPaid: "Fully paid",
      byUser: "By: {user} on {date}",
      earnings: "Earnings",
      totalEarnings: "Total earnings:",
      totalDeductionsColon: "Total deductions:",
      baseSalaryColon: "Base salary:",
      netSalaryColon: "Net salary:",
      bonusesAllowances: "Bonuses, incentives & allowances:",
      allowancesColon: "Allowances:",
      deductionsColon: "Deductions:",
      monthlyLateHours: "Monthly late hours",
      totalInDays: "Total in {days} days",
      deductionPerHour: "Deduction rate/hour",
      monthDeduction: "Month deduction",
      actualWorkDays: "Actual work days (excl. holidays):",
      daysCount: "{count} days"
    },
    ui: {
      overview: "Overview",
      attendanceCheckout: "Attendance & Check-out",
      skillsAndCertificates: "Skills & Certificates",
      messaging: "Messaging",
      myDocuments: "My Documents",
      holidays: "Holidays",
      tasks: "Tasks",
      welcome: "Welcome,",
      employeeNumber: "Employee No.",
      joined: "Joined:",
      directManager: "Direct Manager:",
      todayStatus: "Today's Status",
      checkInLabel: "In:",
      netSalary: "Net Salary",
      attendanceRate: "Attendance Rate",
      performanceRating: "Performance Rating",
      todayBreakTime: "Today's Break Time",
      currentlyOnBreak: "Currently on break",
      completedTasks: "Completed Tasks",
      recentNotifications: "Recent Notifications",
      recentRequests: "Recent Requests",
      attendanceStatus: "Attendance Status",
      status: "Status:",
      totalTime: "Total Time:",
      activeTime: "Active Time:",
      idleTime: "Idle Time:",
      checkIn: "Check In",
      checkOut: "Check Out",
      checkInEmoji: "🟢 Check In",
      checkOutEmoji: "🔴 Check Out",
      break: "Break",
      startBreak: "Start Break",
      endBreak: "End Break",
      breakNotes: "Break Notes",
      breakNotesCount: "Break Notes ({count})",
      viewBreakNotes: "View break notes ({count} notes)",
      breakDuration: "Break Duration:",
      breakTime: "Break Time:",
      currentlyOnBreakColon: "Currently on break:",
      startedAt: "Started at:",
      workingAlready: "Work already in progress",
      workingInProgress: "Work in progress...",
      onBreakHint: "On break...",
      canStartWorkNow: "You can start work now",
      workingCanPauseOrBreak: "Work in progress - you can pause or take a break",
      workPausedCanResumeOrEnd: "Work paused - you can resume or end",
      onBreakCanEndOrWork: "On break - you can end the break or work",
      canControlFromHere: "You can control the app from here",
      onTime: "On time",
      late: "Late",
      absent: "Absent",
      onBreak: "On break",
      present: "Present on time",
      weekend: "Weekend",
      unavailable: "Unavailable",
      holiday: "Holiday",
      leave: "Leave",
      officialHoliday: "Official Holiday",
      offline: "Disconnected",
      connected: "Connected",
      activeWorking: "Active - currently working",
      paused: "Paused",
      connectedReady: "Connected - ready to work",
      appStatus: "App Status",
      todayWorkTime: "Today's work time:",
      productivity: "Productivity:",
      workPausedHint: "Work is paused - you can resume or end",
      appReadyHint: "App is connected and ready to start work",
      appOfflineHint: "App is disconnected - please start the desktop app",
      timeTracking: "Time Tracking",
      refresh: "Refresh",
      retry: "Retry",
      verifyMatch: "Verify match",
      updateTodayRecord: "Update today's record",
      endWork: "End Work",
      resumeWork: "Resume Work",
      startWork: "Start Work",
      pause: "Pause",
      resume: "Resume",
      remoteControl: "Remote Control",
      useDesktopAppHint: "Use the desktop app for the best tracking experience",
      todayActivity: "Today's Activity",
      productivityRate: "Productivity Rate:",
      lastActivity: "Last activity:",
      noActivityToday: "No activity today",
      monitoring: "Monitoring",
      monitoringMode: "Monitoring Mode:",
      active: "Active",
      screenshots: "Screenshots",
      recentScreenshots: "Recent Screenshots",
      noScreenshots: "No screenshots available",
      ensureDesktopRunning: "Make sure the desktop app is running",
      syncing: "Syncing...",
      syncNow: "Sync data now",
      totalBreakTime: "Total Break Time",
      lastImage: "Last Image:",
      view: "View",
      noImages: "No images",
      connectedNow: "Connected now",
      verifyDataMatch: "Verify data match",
      clear: "Clear",
      export: "Export",
      viewOnlyNoClear: "View only - cannot clear",
      updateMonth: "Update Month",
      date: "Date",
      day: "Day",
      remainingTime: "Remaining Time",
      productivityCol: "Productivity",
      statusCol: "Status",
      dataCol: "Data",
      today: "Today",
      weeklyStats: "Weekly Stats",
      avgProductivity: "Avg. Productivity",
      totalWork: "Total Work",
      workDays: "Work Days",
      hours: "hours",
      minutes: "minutes",
      detailedDailyLog: "Detailed Daily Log",
      notLoggedIn: "Not logged in",
      pleaseLogin: "Please log in to access this page",
      loadingData: "Loading data...",
      pleaseWait: "Please wait",
      loadingTrackingData: "Loading tracking data...",
      fetchingLatestFromDesktop: "Fetching the latest data from the desktop app",
      fetchDataError: "Error fetching data",
      verifyAndSyncTitle: "Verify data match and sync",
      createUpdateTodayRecordTitle: "Create/update today's record",
      desktopTimeTrackingApp: "Desktop Time Tracking App",
      desktopAppFeatures: "Precise time tracking • Productivity analysis • Task management",
      completelyFree: "Completely free",
      easyToUse: "Easy to use",
      instantSync: "Instant sync",
      downloadApp: "Download App",
      compatibleWithOs: "Compatible with Windows, Mac, Linux",
      weeklyStatsDesc: "Track work hours and productivity over the past week",
      productivityRateLabel: "Productivity rate",
      workTimeLabel: "Work time",
      activeTimeLabel: "Active time",
      dayLabel: "Day {day}",
      productivityPercentLabel: "Productivity rate %",
      workTimeHoursLabel: "Work time (hours)",
      activeTimeHoursLabel: "Active time (hours)",
      filters: "Filters",
      screenshotsProtectedDesc: "Random screenshots are taken periodically to verify actual presence — data is protected and cannot be cleared",
      search: "Search",
      searchImagesPlaceholder: "Search images...",
      activityType: "Activity type",
      allActivities: "All activities",
      activeWork: "Active work",
      idle: "Idle",
      timePeriod: "Time period",
      allTimes: "All times",
      morningRange: "Morning (6 AM – 12 PM)",
      afternoonRange: "Afternoon (12 PM – 6 PM)",
      eveningRange: "Evening (6 PM – 6 AM)",
      productivityLevel: "Productivity level",
      allLevels: "All levels",
      highProductivity: "High (80%+)",
      mediumProductivity: "Medium (50%–80%)",
      lowProductivity: "Low (under 50%)",
      dateRange: "Date range",
      fromDate: "From date",
      toDate: "To date",
      reset: "Reset",
      ofImages: "{filtered} of {total} images",
      applyFilters: "Apply filters",
      totalImagesCount: "Total images: {count}",
      pageOf: "Page {current} of {total}",
      showingRange: "Showing {start}-{end} of {total}",
      screenshotN: "Screenshot {n}",
      screenshot: "Screenshot",
      workActivity: "Work activity",
      previous: "Previous",
      next: "Next",
      liveConnected: "Live connected",
      sameAppData: "Same app data",
      updateMonthTitle: "Update all month records (including today) from app data",
      liveConnectedDesc: "Live connected to the desktop app — data matches 100% with the app (total, active, idle, break)",
      loadingOrDefaultData: "Loading data or using default data",
      showingDefaultData: "Showing default data — make sure the desktop app is running",
      zeroMinutes: "0 minutes",
      hoursAndMinutes: "{hours} hours {minutes} minutes",
      minutesOnly: "{minutes} minutes",
      completed: "Completed",
      fromApp: "App",
      none: "None",
      realDataDays: "Days with real data",
      totalWorkHours: "Total work hours",
      totalActiveHours: "Total active hours",
      totalIdleTime: "Total idle time",
      dailyLogLinkedToDb: "Detailed daily log is linked directly to the database",
      dataMatch100: "Data matches 100%:",
      dataMatchExample: "If the app shows 4 minutes total and 2 minutes active, you will see the same numbers here in the table",
      totalTimeBullet: "Total time:",
      totalTimeBulletDesc: "Exactly the same number as in the app",
      activeTimeBullet: "Active time:",
      activeTimeBulletDesc: "Same active time as in the app",
      idleTimeBullet: "Idle time:",
      idleTimeBulletDesc: "Same idle time calculated in the app",
      breakTimeBullet: "Break time:",
      breakTimeBulletDesc: "Same break time if breaks were taken",
      updateTodayBullet: "\"Update today\" button:",
      updateTodayBulletDesc: "Updates today's record only from app data",
      updateMonthBullet: "\"Update month\" button:",
      updateMonthBulletDesc: "Updates all current month records from app data",
      appIndicatorBullet: "App indicator:",
      appIndicatorBulletDesc: "Appears for days that have real data from the app",
      desktopMonitoringGuide: "Desktop Monitoring System Guide",
      desktopMonitoringGuideDesc: "Complete guide to using the attendance and productivity monitoring system",
      basicRequirements: "Basic requirements",
      desktopApp: "Desktop app",
      desktopAppDesc: "Download and install the app on your designated work device",
      stableInternet: "Stable internet connection",
      stableInternetDesc: "To ensure data syncs with the server",
      login: "Sign in",
      loginDesc: "Use the same login credentials as the website",
      monitoringAndTracking: "Monitoring and tracking",
      autoActivityTracking: "Automatic activity tracking",
      autoActivityTrackingDesc: "Monitors mouse and keyboard activity to calculate actual work time",
      randomScreenshots: "Random screenshots",
      randomScreenshotsDesc: "Every 10–30 minutes to verify actual presence — protected from deletion",
      productivityCalculation: "Productivity rate calculation",
      productivityCalculationDesc: "Based on the ratio of active time to total time",
      activeAppRequired: "Active app required",
      activeAppRequiredDesc: "You can only check in from the website when the app is connected",
      dualCheckIn: "Dual check-in",
      dualCheckInDesc: "You can check in from the app or the website (app must be connected)",
      preciseTiming: "Precise timing",
      preciseTimingDesc: "Accurate recording of check-in/out times and time periods",
      privacyAndSecurity: "Privacy and security",
      protectedData: "Protected data",
      protectedDataDesc: "All data is encrypted and protected according to security standards",
      viewOnly: "View only",
      viewOnlyDesc: "Employees can only view their own screenshots",
      cannotDelete: "Cannot delete",
      cannotDeleteDesc: "Data is protected and cannot be deleted by the employee",
      importantNotice: "Important notice",
      importantNoticeDesc: "The monitoring system is designed to ensure fairness and transparency in the workplace. All data is used for administrative purposes only and is protected with the highest privacy standards. For any questions, please contact HR.",
      screenshotGallery: "Screenshot Gallery ({count})",
      galleryFilters: "Gallery filters",
      galleryDesc: "All images captured during work — with browsing and filtering",
      galleryImagesCount: "Gallery: {count} images",
      clickToViewFull: "Click any image to view it full size",
      screenshotTitle: "Screenshot - {timestamp}",
      close: "Close",
      screenshotPreview: "Screenshot preview",
      activityColon: "Activity:",
      imageProtected: "This image is protected and cannot be downloaded or deleted",
      noDataAvailable: "No data available",
      noEmployeeProfile: "No employee profile is linked to your account currently.",
      noEmployeeProfileContactAdmin: "No employee profile is linked to your account currently. Please contact administration to add an employee profile.",
      noEmployeeFile: "No employee profile",
      adminNoEmployeeFile: "You are signed in as an admin and do not have an employee profile linked to your account.",
      needEmployeeFileAccess: "To access employee information, you need an employee profile linked to your account.",
      backToDashboard: "Back to dashboard",
      breakReason: "Break reason",
      breakReasonPrompt: "Enter the reason for taking a break (optional):",
      breakReasonPlaceholder: "e.g. lunch, coffee break, phone call...",
      noBreakNotes: "No break notes saved",
      breakNotesWillAppear: "Break notes you record will appear here",
      totalBreaksLogged: "Total breaks logged:",
      deleteThisNote: "Delete this note",
      confirmDeleteAllBreakNotes: "Are you sure you want to delete all break notes?",
      deleteAllNotes: "Delete all notes",
      work: "Work",
      zeroMinShort: "0m",
      minShort: "{m}m",
      hourShort: "{h}h",
      hourMinShort: "{h}h {m}m",
      zeroHoursMinutes: "0 hours 0 minutes",
      verifyDataMatchDetailedTitle: "Verify data match between the app and the detailed table",
      unspecified: "Unspecified",
      loadPartialDataFailed: "Failed to load some data",
      months: {
        1: "January",
        2: "February",
        3: "March",
        4: "April",
        5: "May",
        6: "June",
        7: "July",
        8: "August",
        9: "September",
        10: "October",
        11: "November",
        12: "December"
      }
    },
    msg: {
      companyGroup: "Company Group",
      readOnly: "Read Only",
      title: "My Sent Messages",
      underDevelopment: "Still under development...!",
      loading: "Loading messages...",
      aiEnabled: "AI: On",
      aiDisabled: "AI: Off",
      enableAi: "Enable AI mode",
      disableAi: "Disable AI mode",
      sentCount: "{count} sent messages",
      sortNewest: "Newest first",
      sortOldest: "Oldest first",
      sortAria: "Sort messages",
      searchAria: "Search messages",
      emptyTitle: "No sent messages yet",
      emptyHint: "Messages you send will appear here.",
      noContent: "No content available",
      languageLabel: "Language: {lang}",
      sendFailed: "Failed to send",
      aiFeatures: "AI features",
      translateTo: "Translate to language:",
      languagePlaceholder: "Enter language (e.g. English)",
      translate: "Translate",
      messagePlaceholder: "Write your professional message here...",
      messageAria: "Enter a new message",
      improveTitle: "Improve message wording",
      translateToTitle: "Translate to {lang}",
      sendAria: "Send message",
      translateError: "Error translating message",
      improveError: "Error improving message",
      invalidDate: "Invalid date",
      invalidToken: "Invalid or missing auth token",
      unauthorized: "Unauthorized: please sign in again",
      badRequest: "Invalid request",
      sendFailedStatus: "Failed to send message: {status}",
      sendSuccess: "Message sent successfully",
      sendUnknownError: "Unknown error while sending message",
      you: "You",
      hrTitle: "HR Announcements",
      loadingChat: "Loading Chat...",
      noSearchResults: "No messages found for \"{term}\"",
      noSearchHint: "Try a different search term or clear the search.",
      noAnnouncements: "No announcements yet",
      noAnnouncementsHint: "Messages from administrators will appear here.",
      unknownSender: "Unknown Sender",
      employeesOnlySend: "Only employees can send",
      selectLanguage: "Select language",
      customLanguagePlaceholder: "Enter custom language (e.g., Italian)",
      searchPlaceholder: "Search messages by text or date (e.g., 'March 2023')",
      translatedTo: "Translated to {lang}:",
      read: "Read",
      unread: "Unread",
      newest: "Newest",
      oldest: "Oldest",
      toggleSearch: "Toggle search input",
      toggleTranslate: "Toggle translate input",
      sortBy: "Sort messages",
      selectTranslateAll: "Select language to translate all messages",
      selectTranslateOne: "Select language to translate message",
      submitLanguage: "Submit language",
      submitCustomLanguage: "Submit custom language",
      toggleTranslateDropdown: "Toggle translate dropdown"
    }
  },
  notifications: {
    title: "Notifications Panel",
    fetchFailed: "Failed to fetch notifications",
    fetchError: "An error occurred while fetching notifications",
    markReadFailed: "Failed to update notification status",
    markAllReadFailed: "Failed to update notifications status",
    deleteFailed: "Failed to delete notification",
    deleteAllFailed: "Failed to delete all notifications",
    noNotifications: "No notifications",
    markAllRead: "Mark all as read",
    deleteAll: "Delete all",
    all: "All",
    unread: "Unread",
    read: "Read",
    unreadCount: "{count} unread notification"
  },
  employeesPending: {
    selectShiftRequired: "Please select a shift for the employee",
    invalidEmployeeId: "Invalid employee ID",
    enterAllowanceName: "Please enter allowance name",
    enterValidAllowanceAmount: "Please enter a valid allowance amount",
    selectStartDate: "Please select a start date",
    selectEndDateOrPermanent: "Please select an end date or enable \"Permanent\"",
    allowanceAddedToList: "Allowance added to the list",
    employeeIdMissing: "Employee ID not found",
    selectWorkLocation: "Please select work location",
    enterValidBaseSalary: "Please enter a valid base salary",
    enterValidContractDuration: "Please enter a valid contract duration in months (whole number greater than 0)",
    enterDailyOrders: "Please enter daily orders count (must be 1 or more)",
    enterRejectionReason: "Please enter rejection reason",
    rejectionReasonMin: "Rejection reason must be at least 10 characters",
    rejectionReasonMax: "Rejection reason is too long (max 500 characters)",
    approveSuccess: "Employee approved successfully",
    rejectSuccess: "Employee rejected successfully",
    noEmployeesFound: "No employee data found",
    fetchEmployeesFailed: "Failed to fetch employees from server. Please try again.",
    notAuthorizedFetch: "You are not authorized to fetch employee data",
    whatsappOpened: "WhatsApp Web opened to message {name}",
    whatsappOpenFailed: "An error occurred while opening WhatsApp Web. Please try again.",
    sendWhatsAppMessage: "Send WhatsApp Message",
    sendWhatsAppTo: "Send WhatsApp message to {name}",
    confirmApprove: "Are you sure you want to approve this employee?",
    approving: "Approving...",
    approve: "Approve",
    rejecting: "Rejecting...",
    reject: "Reject",
    pendingApproval: "Pending approval",
    approved: "Approved",
    rejected: "Rejected",
    approveFailed: "Failed to approve employee",
    approveError: "An error occurred while approving the employee"
  },
  companyValidation: {
    htmlInsteadOfJson: "Received HTML instead of JSON. This may be an ngrok page. Please check the connection.",
    parseResponseFailed: "Failed to parse server response",
    corsError: "Server connection error. Please check CORS settings on the server.",
    connectionError: "Server connection error",
    unknownError: "Unknown error",
    enterValidCoordinates: "Please enter valid coordinates",
    latitudeRange: "Latitude must be between -90 and 90",
    longitudeRange: "Longitude must be between -180 and 180",
    browserNoGeolocation: "Browser does not support geolocation",
    getLocationFailed: "Failed to get location",
    enterBranchName: "Please enter branch name",
    branchIdMissing: "Branch ID not found",
    noLocationForBranch: "No location set for this branch",
    invalidOrgIdFormat: "Invalid organization ID format. Must be XX-0000 (e.g. DE-5292). Please check organization settings.",
    orgIdSavedRetry: "Organization ID saved. Please try again.",
    enterDepartmentName: "Please enter department name",
    enterEquipmentName: "Please enter asset name",
    enterValidPrice: "Please enter a valid price",
    selectEmployeeToAssign: "Please select an employee to assign",
    selectNewEmployee: "Please select a new employee to assign",
    documentIdMissing: "Document ID (publicId) not found",
    selectFileToUpload: "Please select a file to upload",
    enterToolName: "Please enter tool name",
    selectAtLeastOneEmployee: "Please select at least one employee"
  },
  deductionsValidation: {
    ruleDelayMinutes: "Rule {n}: delay minutes must be set",
    ruleCustomPenalty: "Rule {n}: custom penalty requires an amount or number of days"
  },
  taxInsurance: {
    enterDeductionNameInRow: "Please enter deduction name in row {n}",
    amountErrorInRow: "{error} (row {n})",
    startBeforeEndInRow: "Start date must be before end date in row {n}",
    saveSuccess: "Tax and insurance settings saved successfully",
    saveFailed: "Failed to save data",
    saveError: "An error occurred while saving data",
    title: "Tax and Insurance Settings",
    description: "Manage fixed deductions for all employees",
    manageHint: "Manage fixed deductions such as taxes, social insurance, and health insurance",
    viewDetails: "View details",
    hide: "Hide",
    addNewDeduction: "Add new deduction",
    addFirstDeduction: "Add first deduction",
    deductionN: "Deduction #{n}",
    deductionName: "Deduction name",
    deductionNamePlaceholder: "e.g. Taxes, social insurance",
    amount: "Amount",
    startDate: "Start date",
    endDateOptional: "End date (leave empty for permanent deduction)",
    monthlyTotal: "Total monthly deductions",
    deductionAmountField: "Deduction amount"
  },
  signup: {
    title: "Create New Account",
    subtitle: "Join the HR management system",
    formTitle: "Create New Account",
    formSubtitle: "Enter your details to create an account",
    username: "Username",
    usernamePlaceholder: "Enter username",
    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    email: "Email",
    emailPlaceholder: "Enter email",
    phone: "Phone Number",
    phonePlaceholder: "Enter phone number",
    department: "Department",
    selectDepartment: "Select department",
    loadingDepartments: "Loading departments...",
    role: "Job Title",
    selectRole: "Select job title",
    trainee: "Trainee",
    employee: "Employee",
    orgId: "Organization Code",
    orgIdHint: "Enter your company code (XX-0000) to load departments",
    enterOrgIdFirst: "Enter the company code first",
    nationalId: "National ID",
    nationalIdPlaceholder: "Enter national ID",
    gender: "Gender",
    selectGender: "Select gender",
    male: "Male",
    female: "Female",
    idFront: "ID Front Image",
    idBack: "ID Back Image",
    password: "Password",
    passwordPlaceholder: "Enter password",
    confirmPassword: "Confirm Password",
    confirmPasswordPlaceholder: "Re-enter password",
    createAccount: "Create Account",
    creating: "Creating account...",
    alreadyHaveAccount: "Already have an account?",
    login: "Sign In",
    joinHr: "Join the HR management system",
    featureDataTitle: "Data Management",
    featureDataDesc: "Update and manage your personal data",
    featureSalaryTitle: "Salary Tracking",
    featureSalaryDesc: "Track salary, allowances, and deductions",
    featureLeaveTitle: "Leave Requests",
    featureLeaveDesc: "Submit and track leave requests",
    featureReportsTitle: "Reports",
    featureReportsDesc: "View reports and statistics",
    validationUsername: "Please enter username",
    validationEmail: "Please enter email",
    validationEmailInvalid: "Please enter a valid email",
    validationPasswordLength: "Password is required",
    validationPasswordMax: "Password must not exceed 128 characters",
    passwordTooCommon: "This password is too common. Choose a stronger one",
    validationOrgId: "Please enter a valid organization code (XX-0000)",
    validationPasswordMismatch: "Password and confirmation do not match",
    validationFirstName: "Please enter first name",
    validationLastName: "Please enter last name",
    validationPhone: "Please enter a valid phone number",
    validationDepartment: "Please select a department",
    validationRole: "Please select a job title",
    validationNationalId: "Please enter national ID",
    validationGender: "Please select gender",
    validationIdFront: "Please upload ID front image",
    validationIdBack: "Please upload ID back image",
    successFallback: "Account created successfully! Awaiting admin approval.",
    errorFallback: "An error occurred while creating the account",
    identifierExists: "An account with this email, phone, username, or national ID already exists",
    departmentsLoadFailed: "Failed to load departments",
    departmentsFetchError: "Error fetching departments. Please try again later."
  },
  payrollStats: {
    title: "Payroll Statistics",
    fetchTitle: "Fetch Payroll Statistics",
    monthOrYear: "Month or Year",
    datePlaceholder: "Select year or month and year",
    selectMonthYear: "Select month and year",
    selectYearOnly: "Select year only",
    dateHint: "Select a year (e.g. 2024) or month and year (e.g. 2024-08). Month is always shown as two digits (01 to 12).",
    fetchStats: "Fetch Statistics",
    willFetchMonth: "Will fetch statistics for month {period}",
    willFetchYear: "Will fetch statistics for year {period}",
    selectDateError: "Please select a year or month and year",
    authError: "Invalid authentication token. Please sign in again.",
    serverError: "Server connection error: {status}",
    fetchFailed: "Failed to fetch payroll statistics",
    invalidResponse: "Invalid response data",
    fetchSuccess: "Payroll statistics fetched successfully",
    notAvailable: "Not available",
    unspecified: "Unspecified",
    employeesInfo: "Employee Information",
    totalEmployees: "Total Employees:",
    withPayroll: "With Payroll:",
    withoutPayroll: "Without Payroll:",
    financialInfo: "Financial Information",
    totalSalaries: "Total Salaries:",
    totalPaid: "Amount Paid:",
    totalRemaining: "Amount Remaining:",
    percentagePaid: "Payment Percentage:",
    statusSummary: "Status Summary",
    paid: "Paid:",
    partiallyPaid: "Partially Paid:",
    pending: "Pending:",
    processing: "Processing:",
    cancelled: "Cancelled:",
    noStatusData: "No status breakdown data",
    payrollPeriod: "Payroll Period",
    selectedMonth: "Selected month",
    selectedYear: "Selected year",
    unspecifiedPeriod: "Unspecified period",
    months: {
      "01": "January",
      "02": "February",
      "03": "March",
      "04": "April",
      "05": "May",
      "06": "June",
      "07": "July",
      "08": "August",
      "09": "September",
      "10": "October",
      "11": "November",
      "12": "December"
    }
  },
  setup: {
    title: "System Setup",
    subtitle: "This setup is essential to configure your system correctly. We recommend completing all steps for the best experience.",
    skip: "Skip",
    skipNow: "Skip now",
    cancel: "Cancel",
    stepLabel: "Step",
    steps: "steps",
    next: "Next",
    back: "Back",
    finish: "Finish",
    saving: "Saving...",
    yes: "Yes",
    no: "No",
    companyInfo: "Company Information",
    workDaysTimezone: "Work Days & Timezone",
    shifts: "Work Shifts",
    shiftsHint: "Add at least one work shift (e.g. Morning) so employees can check in correctly. You can add more later from Settings.",
    shiftsDescription: "Define your company work hours. One shift is enough to start; add more if you run multiple shifts.",
    shiftsRequired: "Add at least one shift to continue",
    location: "Company Locations",
    branches: "Company Branches",
    holidays: "Official Holidays",
    delaySettings: "Delay Settings",
    approvalsRouting: "Approval Routing",
    finishStep: "Setup Complete",
    companyName: "Company Name",
    companyNamePlaceholder: "Enter company name",
    description: "Description",
    email: "Email",
    phone: "Phone",
    website: "Website",
    address: "Address",
    street: "Street",
    workDays: "Work Days",
    workDaysHint: "Select the weekdays your company works",
    workDaysTimezoneHint: "Work days and timezone affect attendance and payroll. Please set them accurately.",
    timezone: "Timezone",
    selectTimezone: "Select timezone",
    timezoneHint: "Select the timezone for your company location",
    locationName: "Location Name",
    locationNamePlaceholder: "e.g. Main Office",
    latitude: "Latitude",
    longitude: "Longitude",
    radius: "Radius (m)",
    useMyLocation: "Use my location",
    mainOffice: "Main Office",
    hasBranches: "Does your company have branches?",
    companyBranches: "Company Branches",
    branchName: "Branch name",
    addBranch: "Add branch",
    wantHolidays: "Do you want to add official holidays?",
    officialHolidays: "Official Holidays",
    holidayName: "Holiday name",
    date: "Date",
    type: "Type",
    selectType: "Select type",
    national: "National",
    religious: "Religious",
    regional: "Regional",
    addHoliday: "Add holiday",
    wantDelay: "Do you want to add delay/late penalty settings?",
    enableLatePenalties: "Enable late penalties",
    delayMinutes: "Delay minutes (from shift start)",
    delayMinutesFromShiftStart: "Note: minutes are counted from shift start — not after the grace period.",
    delayGraceHint: "Current shift grace period: {grace} minutes. Suggested first deduction rule: after {suggested} minutes from shift start.",
    delayMinutesHelp: "Must be greater than grace ({grace}) — minimum {min}.",
    delayMustExceedGrace: "Rule {n}: delay minutes ({delay}) must be greater than grace period ({grace}) — both from shift start",
    delayMinutesRequired: "Rule {n}: enter valid delay minutes (at least 1)",
    penaltyType: "Penalty type",
    selectPenaltyType: "Select penalty type",
    quarterDay: "Quarter day",
    halfDay: "Half day",
    fullDay: "Full day",
    setupComplete: "Your account is set up successfully",
    setupCompleteHint: "You can complete or edit these settings later from the Settings page",
    geolocationNotSupported: "Geolocation not supported",
    locationSet: "Location set",
    getLocationFailed: "Failed to get location",
    saveSuccess: "Saved successfully",
    saveFailed: "Save failed",
    enterBranchName: "Enter branch name",
    branchAdded: "Branch added",
    addBranchFailed: "Failed to add branch",
    enterNameAndDate: "Enter name and date",
    holidayAdded: "Holiday added",
    addHolidayFailed: "Failed to add holiday",
    delaySaved: "Delay settings saved",
    delaySaveFailed: "Failed to save",
    approvalsRoutingQuestion: "Who should receive leave and permission requests (e.g. early departure)?",
    approvalsRoutingHint: "If you skip choosing, team leader is the default. Admin can later Take Over any request from the team leader when needed.",
    approvalsToTeamLeader: "Team leader (recommended)",
    approvalsToTeamLeaderDesc: "Requests go to the department team leader to approve or reject. Admin only intervenes after Take Over.",
    approvalsToAdmin: "Admin only",
    approvalsToAdminDesc: "All leave and permission requests go directly to the admin to approve or reject.",
    sunday: "Sun",
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat"
  },
  leader: {
    employeeUpdateForm: {
      title: "Update employee data",
      name: "Name",
      namePlaceholder: "Enter name",
      email: "Email",
      emailPlaceholder: "Enter email",
      phone: "Phone number",
      phonePlaceholder: "Enter phone number",
      department: "Department",
      departmentPlaceholder: "Enter department",
      position: "Position",
      positionPlaceholder: "Enter position",
      address: "Address",
      addressPlaceholder: "Enter address",
      nationalID: "National ID",
      nationalIDPlaceholder: "Enter national ID",
      baseSalary: "Base salary (SAR)",
      baseSalaryPlaceholder: "Enter base salary",
      experience: "Experience",
      experiencePlaceholder: "Enter experience details",
      education: "Education",
      educationPlaceholder: "Enter education details",
      skills: "Skills (comma-separated)",
      skillsPlaceholder: "Enter skills (e.g. js, python)",
      deductions: "Deductions",
      socialInsurance: "Social insurance (SAR)",
      socialInsurancePlaceholder: "Enter social insurance",
      tax: "Tax (SAR)",
      taxPlaceholder: "Enter tax",
      fetchFailed: "Failed to fetch employee data",
      updateSuccess: "Employee data updated successfully",
      updateFailed: "Failed to update employee data",
      updating: "Updating...",
      submit: "Update employee data"
    }
  },
  phone3d: {
    title: "See TeamIN in Action",
    subtitle: "Explore the mobile app on a Samsung device — Chat and Check-in",
    loading: "Loading 3D view...",
    dragHint: "Drag to rotate"
  }
};
